import React, { useMemo } from 'react'
import dynamic from 'next/dynamic'
import Image from 'next/image'
import { Grid } from '@mantine/core'
import useStyles from './HowMusicBoosts.styles'
import { translate } from '../../../../utilities/language'
import { getAttribution } from '../../../../utilities/content'
import {
  ARTICLE_IMAGE_AVIF,
  ARTICLE_IMAGE_JPEG,
  HOW_MUSIC_BOOSTS_TITLE_HTML,
  HOW_MUSIC_BOOSTS_TITLE_HTML_ES,
  HOW_MUSIC_BOOSTS_TITLE_HTML_FR,
  HOW_MUSIC_BOOSTS_TITLE_HTML_KO,
  HOW_MUSIC_BOOSTS_ARTICLE_PUBLISHED_STR,
} from '../../../../static-data/how-music-boosts-productivity-data'
import { IconExternalLink } from '@tabler/icons'
import {
  PRACTICAL_STREAM_PLAYLIST_PAGE_URL,
  PRACTICAL_STREAM_PLAYLIST_PAGE_URL_ES,
  PRACTICAL_STREAM_PLAYLIST_PAGE_URL_FR,
  PRACTICAL_STREAM_PLAYLIST_PAGE_URL_KO,
  PRACTICAL_STREAM_RESTAURANT_HERO_PAGE_URL,
  PRACTICAL_STREAM_RESTAURANT_HERO_PAGE_URL_ES,
  PRACTICAL_STREAM_RESTAURANT_HERO_PAGE_URL_FR,
  PRACTICAL_STREAM_RESTAURANT_HERO_PAGE_URL_KO,
} from '../../../../static-data/how-the-right-playlist-can-increase-restaurant-sales-data'

const BackToArticle: React.ComponentType<any> = dynamic(
  () => import('../../../controls/BackToArticle'),
  {
    ssr: true,
  }
)
const ReadyToTransform: React.ComponentType<any> = dynamic(
  () => import('../../../controls/ReadyToTransform'),
  {
    ssr: true,
  }
)

export default function HowMusicBoosts(props: any) {
  const { classes } = useStyles()
  const selectedLanguage: any = props?.selectedLanguage
  const avifCompatible = props?.avifCompatible

  const articleImage = useMemo(() => {
    return (
      <Image
        width={512}
        height={279}
        sizes="(max-width: 350px) 256px, 
               (max-width: 430px) 300px, 
               (max-width: 590px) 384px, 
               512px"
        src={avifCompatible ? ARTICLE_IMAGE_AVIF : ARTICLE_IMAGE_JPEG}
        alt=""
        priority={true}
        style={{
          marginTop: '30px',
          borderRadius: '10px',
          display: 'block',
          width: '100%',
          maxWidth: '512px',
          height: 'auto',
        }}
      />
    )
  }, [avifCompatible])

  return (
    <div className={classes.mainGrid}>
      <div className={classes.innerDiv}>
        <Grid columns={24}>
          <Grid.Col md={24} pl={20} pr={20}>
            <BackToArticle
              selectedLanguage={selectedLanguage}
              navigateRoute={props?.navigateRoute}
            />
          </Grid.Col>
          <Grid.Col md={24} pl={20} pr={20}>
            <div
              style={{
                display: 'flex',
                alignItems: 'flex-start',
                justifyContent: 'flex-start',
                flexDirection: 'column',
                height: '100%',
              }}
            >
              <h1 className={classes.standardText + ' ' + classes.h1Tag}>
                {translate(
                  HOW_MUSIC_BOOSTS_TITLE_HTML,
                  HOW_MUSIC_BOOSTS_TITLE_HTML_ES,
                  HOW_MUSIC_BOOSTS_TITLE_HTML_FR,
                  HOW_MUSIC_BOOSTS_TITLE_HTML_KO,
                  selectedLanguage
                )}
              </h1>
              {getAttribution(
                translate(
                  'Editor',
                  'Editora',
                  'Éditrice',
                  '편집자',
                  selectedLanguage
                ),
                HOW_MUSIC_BOOSTS_ARTICLE_PUBLISHED_STR,
                selectedLanguage,
                false
              )}
            </div>
          </Grid.Col>
          <Grid.Col md={24} pl={20} pr={20}>
            <p className={classes.pParagraph + ' ' + classes.spanItalic}>
              {translate(
                <>Understanding the Neuroscience behind Music</>,
                <>Comprender la neurociencia detrás de la música</>,
                <>Comprendre les neurosciences derrière la musique</>,
                <>
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    음악
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    뒤에
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    숨겨진
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    신경과학을
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    이해하기
                  </span>
                </>,
                selectedLanguage
              )}
            </p>
            <p className={classes.pParagraph}>
              {translate(
                <>
                  Picture this: You’re trying to concentrate on an important
                  report, but every keyboard click, whispered conversation, and
                  chair squeak shatters your focus. Meanwhile, your colleague
                  across the desk seems perfectly content working in the
                  oppressive silence.
                </>,
                <>
                  Imagínate esto: estás intentando concentrarte en un informe
                  importante, pero cada clic del teclado, cada conversación
                  susurrada y cada crujido de silla te desconcentran por
                  completo. Mientras tanto, tu compañero de al lado parece estar
                  perfectamente a gusto trabajando en medio de ese silencio
                  opresivo.
                </>,
                <>
                  Imaginez la situation&nbsp;: vous essayez de vous concentrer
                  sur un rapport important, mais chaque clic de clavier, chaque
                  conversation chuchotée et chaque bruit de chaise qui grince
                  brise votre concentration. Pendant ce temps, votre collègue
                  d'en face semble parfaitement à l'aise dans ce silence pesant.
                </>,
                <>
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    상상해
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    보세요.
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    중요한
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    보고서에
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    집중하려고
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    하는데,
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    키보드
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    소리,
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    속삭이는
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    대화
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    소리,
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    의자
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    삐걱거리는
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    소리
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    하나하나가
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    당신의
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    집중력을
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    흐트러뜨립니다.
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    그런데
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    책상
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    건너편에
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    있는
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    동료는
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    숨
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    막힐
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    듯한
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    정적
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    속에서도
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    아무렇지
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    않게
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    일하고
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    있는
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    것
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    같습니다.
                  </span>
                </>,
                selectedLanguage
              )}
            </p>
            {articleImage}
            <hr className={classes.threeDotDivisor} />
            <p className={classes.pParagraph}>
              {translate(
                <>Sound familiar?</>,
                <>¿Te suena familiar?</>,
                <>Cela vous semble familier&nbsp;?</>,
                <>
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    익숙한
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    이야기인가요?
                  </span>
                </>,
                selectedLanguage
              )}
            </p>
            <p className={classes.pParagraph}>
              {translate(
                <>
                  For decades, corporate culture championed the silent office.
                  Quiet workspaces with hushed employees were considered the
                  pinnacle of professionalism and productivity. However,
                  groundbreaking neuroscience research has completely shattered
                  this myth.
                </>,
                <>
                  Durante décadas, la cultura corporativa promovió las oficinas
                  silenciosas. Los espacios de trabajo tranquilos con empleados
                  que guardaban silencio se consideraban el colmo del
                  profesionalismo y la productividad. Sin embargo,
                  investigaciones pioneras en neurociencia han desmentido por
                  completo este mito.
                </>,
                <>
                  Pendant des décennies, la culture d'entreprise a prôné le
                  silence au bureau. Les espaces de travail calmes, où les
                  employés travaillaient en silence, étaient considérés comme le
                  summum du professionnalisme et de la productivité. Cependant,
                  des recherches révolutionnaires en neurosciences ont
                  complètement remis en question cette idée reçue.
                </>,
                <>
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    수십
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    년
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    동안
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    기업
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    문화는
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    조용한
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    사무실을
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    이상적인
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    모델로
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    여겨왔습니다.
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    조용한
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    작업
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    공간과
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    조용히
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    일하는
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    직원들은
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    전문성과
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    생산성의
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    정점으로
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    간주되었습니다.
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    그러나
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    획기적인
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    신경과학
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    연구는
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    이러한
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    통념을
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    완전히
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    뒤집어
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    놓았습니다.
                  </span>
                </>,
                selectedLanguage
              )}
            </p>
            <p className={classes.pParagraph}>
              {translate(
                <>
                  <span className={classes.spanBolder}>Here’s the truth:</span>{' '}
                  Silence isn’t the productivity powerhouse we believed it to
                  be. In many cases, it’s actually a stress trigger that hampers
                  performance.
                </>,
                <>
                  <span className={classes.spanBolder}>
                    Aquí está la verdad:
                  </span>{' '}
                  El silencio no es la fuente de productividad que creíamos que
                  era. En muchos casos, en realidad es un factor que genera
                  estrés y perjudica el rendimiento.
                </>,
                <>
                  <span className={classes.spanBolder}>
                    Voici la vérité&nbsp;:
                  </span>{' '}
                  Le silence n'est pas le moteur de productivité que nous
                  pensions. Dans de nombreux cas, il est en réalité un facteur
                  de stress qui nuit aux performances.
                </>,
                <>
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    진실은
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    이렇습니다.
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    침묵은
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    우리가
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    생각했던
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    것처럼
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    생산성을
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    높이는
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    만능
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    해결책이
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    아닙니다.
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    오히려
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    많은
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    경우에
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    스트레스를
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    유발하여
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    업무
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    효율을
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    떨어뜨리는
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    요인이
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    됩니다.
                  </span>
                </>,
                selectedLanguage
              )}
            </p>
            <p className={classes.pParagraph}>
              {translate(
                <>
                  Strategic{' '}
                  <a
                    className={classes.aHref}
                    target="_blank"
                    href={
                      'https://medium.com/practicalstream/how-the-right-background-music-improves-employee-productivity-in-the-workplace-246f0f0f9949'
                    }
                    title={
                      'How the Right Background Music Improves Employee Productivity in the Workplace'
                    }
                    rel="nofollow noopener noreferrer"
                  >
                    workplace{' '}
                    <span className={classes.spanPre}>
                      music&nbsp;
                      <IconExternalLink />
                    </span>
                  </a>{' '}
                  doesn’t just make offices more pleasant&nbsp;— it
                  fundamentally rewires your brain chemistry for enhanced focus,
                  dramatically reduces stress hormones by up to 61%, and can
                  boost work performance by an astonishing 90%. The science is
                  conclusive, and the evidence is overwhelming.
                  <br />
                  If you want help to find the best company to get a license for
                  music, check this:{' '}
                  <a
                    className={classes.aHref}
                    target="_blank"
                    href={
                      'https://medium.com/practicalstream/top-music-licensing-companies-for-legal-and-hassle-free-use-86b65293a4bc'
                    }
                    title={
                      'Top Music Licensing Companies for Legal and Hassle-Free Use'
                    }
                    rel="nofollow noopener noreferrer"
                  >
                    Top Music Licensing{' '}
                    <span className={classes.spanPre}>
                      Companies&nbsp;
                      <IconExternalLink />
                    </span>
                  </a>
                </>,
                <>
                  <a
                    className={classes.aHref}
                    target="_blank"
                    href={
                      'https://medium.com/practicalstream/how-the-right-background-music-improves-employee-productivity-in-the-workplace-246f0f0f9949'
                    }
                    title={
                      'Cómo la música de fondo adecuada mejora la productividad de los empleados en el lugar de trabajo.'
                    }
                    rel="nofollow noopener noreferrer"
                  >
                    La música estratégica en el lugar de{' '}
                    <span className={classes.spanPre}>
                      trabajo&nbsp;
                      <IconExternalLink />
                    </span>
                  </a>{' '}
                  no solo hace que las oficinas sean más agradables, sino que
                  también modifica fundamentalmente la química cerebral para
                  mejorar la concentración, reduce drásticamente las hormonas
                  del estrés hasta en un 61&nbsp;% y puede aumentar el
                  rendimiento laboral en un asombroso 90&nbsp;%. La ciencia es
                  concluyente y la evidencia es abrumadora.
                  <br />
                  Si necesitas ayuda para encontrar la mejor empresa para
                  obtener una licencia de música, consulta lo siguiente:{' '}
                  <a
                    className={classes.aHref}
                    target="_blank"
                    href={
                      'https://medium.com/practicalstream/top-music-licensing-companies-for-legal-and-hassle-free-use-86b65293a4bc'
                    }
                    title={
                      'Las mejores empresas de licencias musicales para un uso legal y sin complicaciones'
                    }
                    rel="nofollow noopener noreferrer"
                  >
                    Principales empresas de licencias{' '}
                    <span className={classes.spanPre}>
                      musicales&nbsp;
                      <IconExternalLink />
                    </span>
                  </a>
                </>,
                <>
                  <a
                    className={classes.aHref}
                    target="_blank"
                    href={
                      'https://medium.com/practicalstream/how-the-right-background-music-improves-employee-productivity-in-the-workplace-246f0f0f9949'
                    }
                    title={
                      'Comment une musique de fond appropriée améliore la productivité des employés sur le lieu de travail.'
                    }
                    rel="nofollow noopener noreferrer"
                  >
                    Une musique{' '}
                    <span className={classes.spanPre}>
                      d'ambiance&nbsp;
                      <IconExternalLink />
                    </span>
                  </a>{' '}
                  judicieusement choisie ne se contente pas de rendre les
                  bureaux plus agréables&nbsp;: elle modifie en profondeur la
                  chimie du cerveau, améliorant ainsi la concentration,
                  réduisant considérablement les hormones du stress (jusqu'à
                  61&nbsp;%) et pouvant même augmenter les performances
                  professionnelles de manière spectaculaire, jusqu'à 90&nbsp;%.
                  Les études scientifiques sont formelles et les preuves sont
                  irréfutables.
                  <br />
                  Si vous souhaitez obtenir de l'aide pour trouver la meilleure
                  entreprise pour obtenir une licence musicale, consultez
                  ceci&nbsp;:{' '}
                  <a
                    className={classes.aHref}
                    target="_blank"
                    href={
                      'https://medium.com/practicalstream/top-music-licensing-companies-for-legal-and-hassle-free-use-86b65293a4bc'
                    }
                    title={
                      'Top Music Licensing Companies for Legal and Hassle-Free Use'
                    }
                    rel="nofollow noopener noreferrer"
                  >
                    Principales sociétés de licences{' '}
                    <span className={classes.spanPre}>
                      musicales&nbsp;
                      <IconExternalLink />
                    </span>
                  </a>
                </>,
                <>
                  <a
                    className={classes.aHref}
                    target="_blank"
                    href={
                      'https://medium.com/practicalstream/how-the-right-background-music-improves-employee-productivity-in-the-workplace-246f0f0f9949'
                    }
                    title={
                      'How the Right Background Music Improves Employee Productivity in the Workplace'
                    }
                    rel="nofollow noopener noreferrer"
                  >
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      전략적으로
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      활용하는
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      사무실
                    </span>{' '}
                    <span className={classes.spanPre}>
                      음악은&nbsp;
                      <IconExternalLink />
                    </span>
                  </a>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    단순히
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    사무실
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    분위기를
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    좋게
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    만드는
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    것을
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    넘어, 집중력
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    향상을
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    위해
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    뇌
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    화학
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    작용을
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    근본적으로
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    변화시키고,
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    스트레스
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    호르몬을
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    최대
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    61%까지 극적으로
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    감소시키며,
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    업무
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    성과를
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    무려
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    90%까지
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    향상시킬
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    수
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    있습니다.
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    과학적인
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    연구
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    결과는 확실하며,
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    그
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    증거는
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    압도적입니다.
                  </span>
                  <br />
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    음악
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    라이선스를
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    취득하기에
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    가장
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    적합한
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    회사를
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    찾는
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    데
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    도움이
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    필요하다면
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    다음
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    정보를
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    확인하세요.
                  </span>{' '}
                  <a
                    className={classes.aHref}
                    target="_blank"
                    href={
                      'https://medium.com/practicalstream/top-music-licensing-companies-for-legal-and-hassle-free-use-86b65293a4bc'
                    }
                    title={
                      '합법적이고 번거로움 없이 음악을 사용할 수 있는 최고의 음악 라이선스 회사들'
                    }
                    rel="nofollow noopener noreferrer"
                  >
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      최고의
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      음악
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      라이선싱
                    </span>{' '}
                    <span className={classes.spanPre}>
                      회사&nbsp;
                      <IconExternalLink />
                    </span>
                  </a>
                </>,
                selectedLanguage
              )}
            </p>
            <section id="whats-in-this-guide">
              <h2 className={classes.subSection}>
                {translate(
                  <>What You’ll Discover in This Guide</>,
                  <>Lo que descubrirás en esta guía</>,
                  <>Ce que vous découvrirez dans ce guide</>,
                  <>
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      이
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      가이드에서
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      무엇을
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      알게
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      될까요?
                    </span>
                  </>,
                  selectedLanguage
                )}
              </h2>
              <p className={classes.pParagraph}>
                {translate(
                  <>You are here:</>,
                  <>Usted está aquí:</>,
                  <>Vous êtes ici&nbsp;:</>,
                  <>
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      현재
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      위치:
                    </span>
                  </>,
                  selectedLanguage
                )}
              </p>
              <ul className={classes.standardText + ' ' + classes.ulArticles}>
                <li className={classes.liArticle}>
                  {translate(
                    <>How music literally rewires your brain</>,
                    <>Cómo la música literalmente reconfigura tu cerebro</>,
                    <>
                      Comment la musique modifie littéralement la structure de
                      votre cerveau
                    </>,
                    <>
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        음악이
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        문자
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        그대로
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        당신의
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        뇌를
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        어떻게
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        재구성하는지
                      </span>
                    </>,
                    selectedLanguage
                  )}
                </li>
                <li className={classes.liArticle}>
                  {translate(
                    <>The dopamine-productivity connection</>,
                    <>La conexión entre la dopamina y la productividad</>,
                    <>Le lien entre la dopamine et la productivité</>,
                    <>
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        도파민과
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        생산성의
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        연관성
                      </span>
                    </>,
                    selectedLanguage
                  )}
                </li>
                <li className={classes.liArticle}>
                  {translate(
                    <>Why tempo and genre matter more than you think</>,
                    <>
                      Por qué el tempo y el género importan más de lo que crees
                    </>,
                    <>
                      Pourquoi le tempo et le genre musical sont plus importants
                      que vous ne le pensez
                    </>,
                    <>
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        왜
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        템포와
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        장르가
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        당신이
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        생각하는
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        것보다
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        더
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        중요한가
                      </span>
                    </>,
                    selectedLanguage
                  )}
                </li>
                <li className={classes.liArticle}>
                  {translate(
                    <>The lyrics problem everyone gets wrong</>,
                    <>El problema de las letras todas se equivocan</>,
                    <>
                      Le problème des paroles de chansons que tout le monde
                      comprend mal
                    </>,
                    <>
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        모든
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        사람들이
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        틀리는
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        가사
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        문제
                      </span>
                    </>,
                    selectedLanguage
                  )}
                </li>
                <li className={classes.liArticle}>
                  {translate(
                    <>How music transforms employee morale</>,
                    <>Cómo la música transforma la moral de los empleados</>,
                    <>Comment la musique transforme le moral des employés</>,
                    <>
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        음악이
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        직원
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        사기에
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        어떤
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        영향을
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        미치는지
                      </span>
                    </>,
                    selectedLanguage
                  )}
                </li>
              </ul>
              <p className={classes.pParagraph}>
                {translate(
                  <>
                    Let’s dive into the fascinating science of sound at work.
                  </>,
                  <>
                    Adentrémonos en la fascinante ciencia del sonido en acción.
                  </>,
                  <>
                    Plongeons-nous dans la science fascinante du son en action.
                  </>,
                  <>
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      이제
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      소리의
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      작동
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      원리에
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      대한
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      흥미로운
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      과학적
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      사실들을
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      자세히
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      살펴보겠습니다.
                    </span>
                  </>,
                  selectedLanguage
                )}
              </p>
            </section>
            <section id="music-rewires-brains">
              <h2 className={classes.subSection}>
                {translate(
                  <>
                    How Music Actually Rewires Your Brain for Peak Performance
                  </>,
                  <>
                    Cómo la música reconfigura tu cerebro para alcanzar el
                    máximo rendimiento
                  </>,
                  <>
                    Comment la musique modifie réellement votre cerveau pour
                    optimiser vos performances
                  </>,
                  <>
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      음악이
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      실제로
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      당신의
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      두뇌를
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      재구성하여
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      최고의
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      성과를
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      내도록
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      하는
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      방법
                    </span>
                  </>,
                  selectedLanguage
                )}
              </h2>
              <p className={classes.pParagraph}>
                {translate(
                  <>
                    When you{' '}
                    <a
                      className={classes.aHref}
                      target="_blank"
                      href={PRACTICAL_STREAM_PLAYLIST_PAGE_URL}
                      title={
                        'Playlists for Background Music that Boost Workplace Productivity'
                      }
                      rel="noopener noreferrer"
                    >
                      press&nbsp;play&nbsp;on&nbsp;a productivity{' '}
                      <span className={classes.spanPre}>
                        playlist&nbsp;
                        <IconExternalLink />
                      </span>
                    </a>{' '}
                    something extraordinary happens inside your skull. Music
                    doesn’t just provide pleasant background noise&nbsp;— it
                    triggers a cascade of neurological events that fundamentally
                    alter how your brain functions.
                  </>,
                  <>
                    Cuando le das al botón de reproducir en una{' '}
                    <a
                      className={classes.aHref}
                      target="_blank"
                      href={PRACTICAL_STREAM_PLAYLIST_PAGE_URL_ES}
                      title={
                        'Listas de reproducción de música ambiental que aumentan la productividad en el lugar de trabajo'
                      }
                      rel="noopener noreferrer"
                    >
                      lista de&nbsp;reproducción para&nbsp;aumentar&nbsp;la{' '}
                      <span className={classes.spanPre}>
                        productividad&nbsp;
                        <IconExternalLink />
                      </span>
                    </a>{' '}
                    ocurre algo extraordinario dentro de tu cerebro. La música
                    no solo proporciona un agradable sonido de fondo, sino que
                    desencadena una cascada de eventos neurológicos que alteran
                    fundamentalmente el funcionamiento de tu cerebro.
                  </>,
                  <>
                    Lorsque vous{' '}
                    <a
                      className={classes.aHref}
                      target="_blank"
                      href={PRACTICAL_STREAM_PLAYLIST_PAGE_URL_FR}
                      title={
                        "Listes de lecture de musique d'ambiance qui stimulent la productivité au travail"
                      }
                      rel="noopener noreferrer"
                    >
                      lancez&nbsp;une playlist&nbsp;conçue pour&nbsp;la{' '}
                      <span className={classes.spanPre}>
                        productivité&nbsp;
                        <IconExternalLink />
                      </span>
                    </a>
                    , quelque chose d'extraordinaire se produit. La musique ne
                    se contente pas de fournir un agréable fond sonore&nbsp;:
                    elle déclenche une cascade d'événements neurologiques qui
                    modifient fondamentalement le fonctionnement de votre
                    cerveau.
                  </>,
                  <>
                    <a
                      className={classes.aHref}
                      target="_blank"
                      href={PRACTICAL_STREAM_PLAYLIST_PAGE_URL_KO}
                      title={'직장 생산성을 높이는 배경 음악 플레이리스트'}
                      rel="noopener noreferrer"
                    >
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        생산성
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        향상
                      </span>{' '}
                      <span className={classes.spanPre}>
                        플레이리스트를&nbsp;
                        <IconExternalLink />
                      </span>
                    </a>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      재생하면
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      머릿속에서
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      놀라운
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      일이
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      벌어집니다.
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      음악은
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      단순히
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      듣기
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      좋은
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      배경
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      소음을
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      제공하는
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      것이
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      아니라,
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      뇌
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      기능
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      방식을
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      근본적으로
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      바꾸는
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      일련의
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      신경학적
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      반응을
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      유발합니다.
                    </span>
                  </>,
                  selectedLanguage
                )}
              </p>
              <p className={classes.pParagraph}>
                {translate(
                  <>
                    Neuroimaging studies reveal that music activates nearly
                    every region of the brain simultaneously. Your limbic system
                    (which controls motivation and reward) lights up. Your
                    hippocampus (responsible for memory formation and retrieval)
                    becomes more active. Your prefrontal cortex (handling
                    complex problem-solving and executive function) engages more
                    efficiently.
                  </>,
                  <>
                    Los estudios de neuroimagen revelan que la música activa
                    casi todas las regiones del cerebro simultáneamente. El
                    sistema límbico (que controla la motivación y la recompensa)
                    se activa. El hipocampo (responsable de la formación y
                    recuperación de la memoria) se vuelve más activo. La corteza
                    prefrontal (encargada de la resolución de problemas
                    complejos y las funciones ejecutivas) funciona de manera más
                    eficiente.
                  </>,
                  <>
                    Les études de neuroimagerie révèlent que la musique active
                    simultanément presque toutes les régions du cerveau. Votre
                    système limbique (qui contrôle la motivation et la
                    récompense) s'active. Votre hippocampe (responsable de la
                    formation et de la récupération des souvenirs) devient plus
                    actif. Votre cortex préfrontal (qui gère la résolution de
                    problèmes complexes et les fonctions exécutives) fonctionne
                    de manière plus efficace.
                  </>,
                  <>
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      신경영상
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      연구에
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      따르면
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      음악은
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      뇌의
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      거의
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      모든
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      영역을
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      동시에
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      활성화시킵니다.
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      동기
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      부여와
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      보상을
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      담당하는
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      변연계가
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      활성화되고,
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      기억
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      형성
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      및
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      검색을
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      담당하는
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      해마가
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      더욱
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      활발해집니다.
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      복잡한
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      문제
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      해결
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      및
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      실행
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      기능을
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      담당하는
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      전전두엽
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      피질도
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      더욱
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      효율적으로
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      작동합니다.
                    </span>
                  </>,
                  selectedLanguage
                )}
              </p>
              <p className={classes.pParagraph}>
                {translate(
                  <>
                    This isn’t abstract psychology or motivational theory. It’s
                    measurable, observable neurochemistry happening in
                    real-time.
                  </>,
                  <>
                    Esto no es psicología abstracta ni teoría de la motivación.
                    Se trata de neuroquímica medible y observable que ocurre en
                    tiempo real.
                  </>,
                  <>
                    Il ne s'agit pas de psychologie abstraite ni de théorie de
                    la motivation. Il s'agit de neurochimie mesurable et
                    observable, qui se déroule en temps réel.
                  </>,
                  <>
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      이것은
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      추상적인
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      심리학이나
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      동기
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      부여
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      이론이
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      아닙니다.
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      실시간으로
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      일어나는
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      측정
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      가능하고
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      관찰
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      가능한
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      신경화학적
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      과정입니다.
                    </span>
                  </>,
                  selectedLanguage
                )}
              </p>
            </section>
            <hr className={classes.threeDotDivisor} />
            <section id="music-rewires-brains">
              <h2 className={classes.subSection}>
                {translate(
                  <>The Dopamine-Focus Connection That Changes Everything</>,
                  <>
                    La conexión entre la dopamina y la concentración que lo
                    cambia todo
                  </>,
                  <>
                    Le lien entre la dopamine et la concentration qui change
                    tout.
                  </>,
                  <>
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      모든
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      것을
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      바꾸는
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      도파민과
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      집중력의
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      연관성
                    </span>
                  </>,
                  selectedLanguage
                )}
              </h2>
              <p className={classes.pParagraph}>
                {translate(
                  <>
                    Here’s where the science gets truly fascinating. Research
                    demonstrates that 90% of workers perform better when
                    listening to music, with 88% producing more accurate work.
                    These aren’t marginal improvements&nbsp;— they’re dramatic
                    performance enhancements that any business leader would
                    pursue aggressively.
                  </>,
                  <>
                    Aquí es donde la ciencia se vuelve realmente fascinante. Las
                    investigaciones demuestran que el 90% de los trabajadores
                    rinde mejor al escuchar música, y el 88% realiza un trabajo
                    más preciso. No se trata de mejoras insignificantes, sino de
                    aumentos drásticos en el rendimiento que cualquier líder
                    empresarial buscaría con ahínco.
                  </>,
                  <>
                    C’est là que la science devient vraiment fascinante. Des
                    recherches démontrent que 90&nbsp;% des travailleurs sont
                    plus performants lorsqu’ils écoutent de la musique, et que
                    88&nbsp;% d’entre eux produisent un travail plus précis. Il
                    ne s’agit pas d’améliorations minimes, mais d’une
                    augmentation spectaculaire des performances que tout
                    dirigeant d’entreprise chercherait à obtenir à tout prix.
                  </>,
                  <>
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      바로
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      이
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      지점에서
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      과학적인
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      사실이
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      정말
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      흥미로워집니다.
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      연구
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      결과에
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      따르면 직원들의
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      90%가
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      음악을
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      들으면서
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      업무
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      효율이
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      향상되고,
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      88%는
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      더
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      정확한 업무를
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      수행하는
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      것으로
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      나타났습니다.
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      이는
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      미미한
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      개선이
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      아니라,
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      모든 기업
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      경영자가
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      적극적으로
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      추구할
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      만한
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      극적인
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      성과
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      향상입니다.
                    </span>
                  </>,
                  selectedLanguage
                )}
              </p>
              <p className={classes.pParagraph}>
                {translate(
                  <>
                    The mechanism behind this improvement centers on dopamine,
                    your brain’s primary motivation and reward neurotransmitter.
                  </>,
                  <>
                    El mecanismo que explica esta mejora se centra en la
                    dopamina, el principal neurotransmisor de motivación y
                    recompensa del cerebro.
                  </>,
                  <>
                    Le mécanisme à l'origine de cette amélioration repose sur la
                    dopamine, le principal neurotransmetteur de la motivation et
                    de la récompense dans le cerveau.
                  </>,
                  <>
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      이러한
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      개선의
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      핵심
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      메커니즘은
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      뇌의
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      주요
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      동기
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      부여
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      및
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      보상
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      신경전달물질인
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      도파민에
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      있습니다.
                    </span>
                  </>,
                  selectedLanguage
                )}
              </p>
              <span className={classes.subSection}>
                {translate(
                  <>Here’s what happens when the right music plays:</>,
                  <>Esto es lo que ocurre cuando suena la música adecuada:</>,
                  <>
                    Voici ce qui se passe lorsque la bonne musique est
                    jouée&nbsp;:
                  </>,
                  <>
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      적절한
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      음악이
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      흘러나올
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      때
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      어떤
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      일이
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      일어나는지
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      보여드리겠습니다.
                    </span>
                  </>,
                  selectedLanguage
                )}
              </span>
              <p className={classes.pParagraph}>
                {translate(
                  <>
                    Music triggers dopamine release in your brain’s nucleus
                    accumbens (reward center). This neurochemical cascade
                    doesn’t just make you feel good&nbsp;— it directly enhances
                    cognitive function. Dopamine sharpens mental focus and
                    clarity, amplifies motivation and goal-directed behavior,
                    improves information processing speed, and enhances working
                    memory capacity.
                  </>,
                  <>
                    La música desencadena la liberación de dopamina en el núcleo
                    accumbens del cerebro (el centro de recompensa). Esta
                    cascada neuroquímica no solo nos hace sentir bien, sino que
                    también mejora directamente la función cognitiva. La
                    dopamina agudiza la concentración y la claridad mental,
                    aumenta la motivación y el comportamiento orientado a
                    objetivos, mejora la velocidad de procesamiento de la
                    información y potencia la capacidad de la memoria de
                    trabajo.
                  </>,
                  <>
                    La musique déclenche la libération de dopamine dans le noyau
                    accumbens de votre cerveau (centre de la récompense). Cette
                    cascade neurochimique ne se contente pas de vous procurer
                    une sensation de bien-être&nbsp;; elle améliore directement
                    les fonctions cognitives. La dopamine aiguise la
                    concentration et la clarté mentale, renforce la motivation
                    et les comportements orientés vers un but, accélère le
                    traitement de l'information et améliore la capacité de la
                    mémoire de travail.
                  </>,
                  <>
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      음악은
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      뇌의
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      측좌핵(보상
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      센터)에서
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      도파민
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      분비를
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      촉진합니다.
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      이러한
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      신경화학적
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      반응은
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      단순히
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      기분을
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      좋게
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      만드는
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      것을
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      넘어
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      인지
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      기능을
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      직접적으로
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      향상시킵니다.
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      도파민은
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      정신
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      집중력과
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      명료성을
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      높이고,
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      동기
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      부여와
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      목표
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      지향적
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      행동을
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      강화하며,
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      정보
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      처리
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      속도를
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      향상시키고,
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      작업
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      기억
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      용량을
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      증진시킵니다.
                    </span>
                  </>,
                  selectedLanguage
                )}
              </p>
              <p className={classes.pParagraph}>
                {translate(
                  <>
                    The right musical tempo can actually regulate how quickly
                    your brain processes information. Fast-paced music
                    accelerates cognitive processing for routine tasks. Slower
                    tempos promote deeper, more methodical thinking for complex
                    problems.
                  </>,
                  <>
                    El tempo musical adecuado puede regular la velocidad con la
                    que el cerebro procesa la información. La música con un
                    ritmo rápido acelera el procesamiento cognitivo para tareas
                    rutinarias, mientras que los ritmos más lentos favorecen un
                    pensamiento más profundo y metódico para problemas
                    complejos.
                  </>,
                  <>
                    Le bon tempo musical peut en réalité réguler la vitesse à
                    laquelle votre cerveau traite l'information. Une musique au
                    rythme rapide accélère le traitement cognitif des tâches
                    routinières. Des tempos plus lents favorisent une réflexion
                    plus approfondie et plus méthodique pour les problèmes
                    complexes.
                  </>,
                  <>
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      적절한
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      음악
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      템포는
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      실제로
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      뇌가
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      정보를
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      처리하는
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      속도를
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      조절할
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      수
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      있습니다.
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      빠른
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      템포의
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      음악은
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      일상적인
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      작업을
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      위한
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      인지
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      처리를
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      가속화하는
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      반면,
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      느린
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      템포는
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      복잡한
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      문제에
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      대해
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      더
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      깊이
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      있고
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      체계적인
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      사고를
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      촉진합니다.
                    </span>
                  </>,
                  selectedLanguage
                )}
              </p>
              <p className={classes.pParagraph}>
                {translate(
                  <>
                    Predictable musical patterns serve another critical
                    function: they reduce cognitive load for repetitive work.
                    When your brain can anticipate musical patterns, it requires
                    less mental energy to process the sound, freeing up
                    cognitive resources for your actual work tasks.
                  </>,
                  <>
                    Los patrones musicales predecibles cumplen otra función
                    fundamental: reducen la carga cognitiva en tareas
                    repetitivas. Cuando el cerebro puede anticipar los patrones
                    musicales, necesita menos energía mental para procesar el
                    sonido, lo que libera recursos cognitivos para las tareas
                    laborales propiamente dichas.
                  </>,
                  <>
                    Les schémas musicaux prévisibles remplissent une autre
                    fonction essentielle&nbsp;: ils réduisent la charge
                    cognitive lors des tâches répétitives. Lorsque votre cerveau
                    peut anticiper les motifs musicaux, il a besoin de moins
                    d'énergie mentale pour traiter le son, ce qui libère des
                    ressources cognitives pour vos tâches professionnelles.
                  </>,
                  <>
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      예측
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      가능한
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      음악
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      패턴은
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      또
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      다른
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      중요한
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      기능을
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      합니다. 바로
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      반복적인
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      작업에서
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      인지적
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      부담을
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      줄여준다는
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      것입니다. 뇌가
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      음악
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      패턴을
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      예측할
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      수
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      있게
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      되면
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      소리를
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      처리하는
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      데
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      필요한
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      정신적
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      에너지가
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      줄어들어
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      실제
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      업무에
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      필요한
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      인지
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      자원을
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      더
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      많이
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      활용할
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      수
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      있게
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      됩니다.
                    </span>
                  </>,
                  selectedLanguage
                )}
              </p>
              <p className={classes.pParagraph}>
                {translate(
                  <>
                    Think of it like this: Your brain has a limited pool of
                    attention and processing power.
                  </>,
                  <>
                    Piénsalo de esta manera: tu cerebro tiene una cantidad
                    limitada de atención y capacidad de procesamiento.
                  </>,
                  <>
                    Voyez les choses de cette façon&nbsp;: votre cerveau dispose
                    d'une quantité limitée d'attention et de capacité de
                    traitement.
                  </>,
                  <>
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      이렇게
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      생각해
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      보세요.
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      당신의
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      뇌는
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      제한된
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      양의
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      주의력과
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      처리
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      능력을
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      가지고
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      있습니다.
                    </span>
                  </>,
                  selectedLanguage
                )}
              </p>
              <p className={classes.pParagraph}>
                {translate(
                  <>
                    Random office noises (conversations, phone rings, keyboard
                    clicks) constantly drain this pool because they’re
                    unpredictable and potentially important. Your brain must
                    constantly evaluate each sound: “Is this something I need to
                    pay attention to?”
                  </>,
                  <>
                    Los ruidos aleatorios de la oficina (conversaciones, timbres
                    de teléfono, clics del teclado) agotan constantemente esta
                    reserva de energía mental, ya que son impredecibles y
                    potencialmente importantes. Tu cerebro debe evaluar
                    constantemente cada sonido: "¿Es esto algo a lo que debo
                    prestar atención?".
                  </>,
                  <>
                    Les bruits aléatoires du bureau (conversations, sonneries de
                    téléphone, clics de clavier) puisent constamment dans cette
                    réserve d'attention, car ils sont imprévisibles et
                    potentiellement importants. Votre cerveau doit évaluer en
                    permanence chaque son&nbsp;: «&nbsp;Est-ce quelque chose à
                    quoi je dois prêter attention&nbsp;?&nbsp;»
                  </>,
                  <>
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      사무실에서
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      발생하는
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      무작위
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      소음(대화
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      소리,
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      전화벨
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      소리,
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      키보드
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      소리
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      등)은
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      예측할
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      수
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      없고
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      잠재적으로
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      중요할
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      수
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      있기
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      때문에
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      끊임없이
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      집중력을
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      소모시킵니다.
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      당신의
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      뇌는
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      각
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      소리를
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      끊임없이
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      평가해야
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      합니다.
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      "이
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      소리에
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      집중해야
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      할까?"
                    </span>
                  </>,
                  selectedLanguage
                )}
              </p>
              <p className={classes.pParagraph}>
                {translate(
                  <>
                    Strategic background music creates a consistent auditory
                    environment that your brain can safely ignore while still
                    masking those disruptive, random noises. The result? More
                    cognitive resources are available for focused work.
                  </>,
                  <>
                    La música de fondo estratégicamente seleccionada crea un
                    entorno auditivo constante que el cerebro puede ignorar sin
                    esfuerzo, a la vez que enmascara los ruidos aleatorios y
                    molestos. ¿El resultado? Se liberan más recursos cognitivos
                    para concentrarse en el trabajo.
                  </>,
                  <>
                    Une musique d'ambiance stratégiquement choisie crée un
                    environnement sonore cohérent que votre cerveau peut ignorer
                    sans effort, tout en masquant les bruits parasites et
                    aléatoires. Résultat&nbsp;: davantage de ressources
                    cognitives sont disponibles pour un travail concentré.
                  </>,
                  <>
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      전략적으로
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      선택된
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      배경
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      음악은
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      뇌가
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      안전하게
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      무시할
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      수
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      있는
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      일관된
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      청각
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      환경을
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      조성하면서도
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      방해가
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      되는
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      불규칙한
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      소음을
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      차단해
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      줍니다.
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      그
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      결과,
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      집중적인
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      업무에
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      더
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      많은
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      인지
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      자원을
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      활용할
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      수
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      있게
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      됩니다.
                    </span>
                  </>,
                  selectedLanguage
                )}
              </p>{' '}
            </section>
            <hr className={classes.threeDotDivisor} />
            <section id="music-and-stress-reduction">
              <h2 className={classes.subSection}>
                {translate(
                  <>The Stress-Reduction Mechanism You’ve Been Missing</>,
                  <>
                    El mecanismo para reducir el estrés que te ha estado
                    faltando
                  </>,
                  <>Le mécanisme de réduction du stress qui vous manquait</>,
                  <>
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      당신이
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      놓치고
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      있었던
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      스트레스
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      감소
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      메커니즘
                    </span>
                  </>,
                  selectedLanguage
                )}
              </h2>
              <p className={classes.pParagraph}>
                {translate(
                  <>
                    Beyond focus and motivation, music fundamentally alters your
                    physiological stress response. Studies published in
                    Frontiers in Psychology found that listening to music can
                    lower cortisol levels&nbsp;— your body’s primary stress
                    hormone&nbsp;— by up to 61%.
                  </>,
                  <>
                    Más allá de la concentración y la motivación, la música
                    altera fundamentalmente la respuesta fisiológica al estrés.
                    Estudios publicados en la revista Frontiers in Psychology
                    revelaron que escuchar música puede reducir los niveles de
                    cortisol&nbsp;— la principal hormona del estrés&nbsp;— hasta
                    en un 61%.
                  </>,
                  <>
                    Au-delà de la concentration et de la motivation, la musique
                    modifie fondamentalement votre réponse physiologique au
                    stress. Des études publiées dans la revue Frontiers in
                    Psychology ont montré que l'écoute de musique peut réduire
                    le taux de cortisol (la principale hormone du stress)
                    jusqu'à 61&nbsp;%.
                  </>,
                  <>
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      집중력과
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      동기
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      부여
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      외에도
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      음악은
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      생리적인
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      스트레스
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      반응을
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      근본적으로
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      변화시킵니다.
                    </span>{' '}
                    <span
                      aria-hidden={true}
                      className={classes.asianTextBlock}
                    ></span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      '프론티어스
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      인
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      사이콜로지(Frontiers
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      in
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      Psychology)'에
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      발표된
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      연구에
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      따르면
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      음악을
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      듣는
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      것은
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      신체의
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      주요
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      스트레스
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      호르몬인
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      코르티솔
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      수치를
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      최대
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      61%까지
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      낮출
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      수
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      있습니다.
                    </span>
                  </>,
                  selectedLanguage
                )}
              </p>
              <p className={classes.pParagraph}>
                {translate(
                  <>
                    This isn’t a minor adjustment. A 61% reduction in cortisol
                    represents a fundamental shift in your body’s stress state.
                  </>,
                  <>
                    Esto no es un ajuste menor. Una reducción del 61% en el
                    cortisol representa un cambio fundamental en el estado de
                    estrés de su cuerpo.
                  </>,
                  <>
                    Il ne s'agit pas d'un ajustement mineur. Une réduction de
                    61&nbsp;% du taux de cortisol représente un changement
                    fondamental dans l'état de stress de votre corps.
                  </>,
                  <>
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      이것은
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      사소한
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      변화가
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      아닙니다.
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      코르티솔
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      수치가
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      61%
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      감소했다는
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      것은
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      신체의
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      스트레스
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      상태에
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      근본적인
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      변화가
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      일어났음을
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      의미합니다.
                    </span>
                  </>,
                  selectedLanguage
                )}
              </p>
            </section>
            <section id="multilayered-stress-reduction">
              <h2 className={classes.subSection}>
                {translate(
                  <>Music’s multi-layered stress-reduction mechanisms:</>,
                  <>
                    Los mecanismos de reducción del estrés de la música, que
                    actúan en múltiples niveles:
                  </>,
                  <>
                    Les mécanismes multicouches de réduction du stress par la
                    musique&nbsp;:
                  </>,
                  <>
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      음악의
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      다층적인
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      스트레스
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      감소
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      메커니즘:
                    </span>
                  </>,
                  selectedLanguage
                )}
              </h2>
              <ol className={classes.standardText + ' ' + ' ol-list-white'}>
                <li
                  className={classes.liOlPoint}
                  style={{
                    marginBottom: '15px',
                  }}
                >
                  {translate(
                    <>
                      <span className={classes.spanBolder}>
                        Cortisol regulation:
                      </span>{' '}
                      Music reduces cortisol production and overall
                      physiological arousal, directly counteracting your body’s
                      stress response.
                    </>,
                    <>
                      <span className={classes.spanBolder}>
                        Regulación del cortisol:
                      </span>{' '}
                      La música reduce la producción de cortisol y la excitación
                      fisiológica general, contrarrestando directamente la
                      respuesta de estrés del cuerpo.
                    </>,
                    <>
                      <span className={classes.spanBolder}>
                        Régulation du cortisol&nbsp;:
                      </span>{' '}
                      La musique réduit la production de cortisol et
                      l'excitation physiologique générale, contrecarrant
                      directement la réponse de votre corps au stress.
                    </>,
                    <>
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        코르티솔
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        조절:
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        음악은
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        코르티솔
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        생성을
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        줄이고
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        전반적인
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        생리적
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        각성
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        상태를
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        낮추어
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        신체의
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        스트레스
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        반응에
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        직접적으로
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        대응합니다.
                      </span>
                    </>,
                    selectedLanguage
                  )}
                </li>
                <li
                  className={classes.liOlPoint}
                  style={{
                    marginBottom: '15px',
                  }}
                >
                  {translate(
                    <>
                      <span className={classes.spanBolder}>
                        Dopamine stimulation:
                      </span>{' '}
                      The same reward pathways that enhance focus also improve
                      emotional regulation and mood stability.
                    </>,
                    <>
                      <span className={classes.spanBolder}>
                        Estimulación de la dopamina:
                      </span>{' '}
                      Las mismas vías de recompensa que mejoran la concentración
                      también mejoran la regulación emocional y la estabilidad
                      del estado de ánimo.
                    </>,
                    <>
                      <span className={classes.spanBolder}>
                        Stimulation de la dopamine&nbsp;:
                      </span>{' '}
                      Les mêmes circuits de récompense qui améliorent la
                      concentration améliorent également la régulation
                      émotionnelle et la stabilité de l'humeur.
                    </>,
                    <>
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        도파민
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        자극:
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        집중력을
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        향상시키는
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        것과
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        동일한
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        보상
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        경로가
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        감정
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        조절
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        및
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        기분
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        안정에도
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        도움을
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        줍니다.
                      </span>
                    </>,
                    selectedLanguage
                  )}
                </li>
                <li
                  className={classes.liOlPoint}
                  style={{
                    marginBottom: '15px',
                  }}
                >
                  {translate(
                    <>
                      <span className={classes.spanBolder}>
                        Parasympathetic activation:
                      </span>{' '}
                      Appropriate music activates your parasympathetic nervous
                      system (your “rest and digest” mode), which decreases
                      heart rate, lowers blood pressure, and promotes
                      physiological calm.
                    </>,
                    <>
                      <span className={classes.spanBolder}>
                        Activación parasimpática:
                      </span>{' '}
                      La música adecuada activa el sistema nervioso
                      parasimpático (el modo de "descanso y digestión"), lo que
                      disminuye la frecuencia cardíaca, reduce la presión
                      arterial y promueve la calma fisiológica.
                    </>,
                    <>
                      <span className={classes.spanBolder}>
                        Activation parasympathique&nbsp;:
                      </span>{' '}
                      Une musique appropriée active votre système nerveux
                      parasympathique (votre mode «&nbsp;repos et
                      digestion&nbsp;»), ce qui diminue la fréquence cardiaque,
                      abaisse la tension artérielle et favorise le calme
                      physiologique.
                    </>,
                    <>
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        부교감
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        신경
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        활성화:
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        적절한
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        음악은
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        부교감
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        신경계(휴식
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        및
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        소화
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        모드)를
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        활성화하여
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        심박수를
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        낮추고
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        혈압을
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        떨어뜨리며
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        생리적
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        안정을
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        촉진합니다.
                      </span>
                    </>,
                    selectedLanguage
                  )}
                </li>
              </ol>
              <p className={classes.pParagraph}>
                {translate(
                  <>
                    Research from the National Library of Medicine demonstrates
                    these effects work regardless of musical preference. Whether
                    employees listen to their personal favorites or carefully
                    curated neutral selections, cortisol levels drop measurably.
                  </>,
                  <>
                    Investigaciones de la Biblioteca Nacional de Medicina
                    demuestran que estos efectos se producen independientemente
                    de las preferencias musicales. Ya sea que los empleados
                    escuchen sus canciones favoritas o selecciones musicales
                    neutras cuidadosamente elegidas, los niveles de cortisol
                    disminuyen de forma significativa.
                  </>,
                  <>
                    Des recherches menées par la Bibliothèque nationale de
                    médecine démontrent que ces effets se manifestent
                    indépendamment des préférences musicales. Que les employés
                    écoutent leurs morceaux préférés ou des sélections musicales
                    neutres soigneusement choisies, le taux de cortisol diminue
                    de manière significative.
                  </>,
                  <>
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      국립
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      의학
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      도서관의
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      연구에
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      따르면
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      이러한
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      효과는
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      음악적
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      취향과
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      관계없이
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      나타납니다.
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      직원들이
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      개인적으로
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      좋아하는
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      음악을
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      듣든,
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      신중하게
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      선별된
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      중립적인
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      음악을
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      듣든
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      관계없이
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      코르티솔
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      수치가
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      눈에
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      띄게
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      감소하는
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      것으로
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      나타났습니다.
                    </span>
                  </>,
                  selectedLanguage
                )}
              </p>
              <p className={classes.pParagraph}>
                {translate(
                  <>
                    Perhaps most remarkably, one study found that 75% of
                    participants shifted to relaxed Alpha brain wave patterns
                    within just 5 minutes of listening to 60 BPM music. Alpha
                    waves represent calm, focused alertness&nbsp;— the ideal
                    mental state for productive knowledge work.
                  </>,
                  <>
                    Quizás lo más sorprendente sea que un estudio reveló que el
                    75% de los participantes experimentaron un cambio hacia
                    patrones de ondas cerebrales alfa, asociados a la
                    relajación, en tan solo 5 minutos de escuchar música a 60
                    pulsaciones por minuto. Las ondas alfa representan un estado
                    de calma y concentración, el estado mental ideal para un
                    trabajo intelectual productivo.
                  </>,
                  <>
                    Fait peut-être le plus remarquable, une étude a révélé que
                    75&nbsp;% des participants passaient à des ondes cérébrales
                    alpha, synonymes de relaxation, en seulement 5 minutes
                    d'écoute de musique à 60 battements par minute. Les ondes
                    alpha représentent un état de calme et de
                    concentration&nbsp;– l'état mental idéal pour un travail
                    intellectuel productif.
                  </>,
                  <>
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      아마도
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      가장
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      주목할
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      만한
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      점은
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      한
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      연구에서
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      참가자의
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      75%가
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      60BPM의
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      음악을
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      들은
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      지
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      단
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      5분
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      만에
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      편안한
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      알파
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      뇌파
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      패턴으로
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      전환되었다는
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      사실입니다.
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      알파파는
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      차분하고
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      집중된
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      경계심을
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      나타내며,
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      이는
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      생산적인
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      지식
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      작업을
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      위한
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      이상적인
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      정신
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      상태입니다.
                    </span>
                  </>,
                  selectedLanguage
                )}
              </p>
              <p className={classes.pParagraph}>
                {translate(
                  <>
                    Consider the implications: workplace stress costs U.S.
                    businesses over $300 billion annually in healthcare
                    expenses, absenteeism, and employee turnover. If strategic
                    music implementation can reduce stress by even 20–30%, the
                    ROI becomes compelling for any organization.
                  </>,
                  <>
                    Consideremos las implicaciones: el estrés laboral le cuesta
                    a las empresas estadounidenses más de 300 mil millones de
                    dólares al año en gastos de atención médica, absentismo y
                    rotación de personal. Si la implementación estratégica de
                    música puede reducir el estrés incluso en un 20-30%, el
                    retorno de la inversión resulta muy atractivo para cualquier
                    organización.
                  </>,
                  <>
                    Considérez les conséquences&nbsp;: le stress au travail
                    coûte aux entreprises américaines plus de 300 milliards de
                    dollars par an en frais de santé, absentéisme et rotation du
                    personnel. Si la mise en place stratégique de la musique
                    peut réduire le stress de 20&nbsp;à&nbsp;30&nbsp;%, le
                    retour sur investissement devient très intéressant pour
                    toute organisation.
                  </>,
                  <>
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      그
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      파급
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      효과를
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      생각해
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      보세요.
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      직장
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      스트레스는
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      미국
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      기업에
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      의료비,
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      결근,
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      직원
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      이직
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      등으로
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      매년
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      3,000억
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      달러
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      이상의
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      손실을
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      초래합니다.
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      전략적인
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      음악
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      활용을
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      통해
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      스트레스를
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      20~30%만
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      줄일
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      수
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      있다면,
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      어떤
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      조직에게든
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      투자
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      대비
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      수익률(ROI)은
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      매우
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      매력적일
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      것입니다.
                    </span>
                  </>,
                  selectedLanguage
                )}
              </p>
            </section>
            <section id="not-all-music-destresses">
              <h2 className={classes.subSection}>
                {translate(
                  <>
                    Not All Music Works: The Science of Tempo, Genre, and Task
                    Alignment
                  </>,
                  <>
                    No toda la música funciona: La ciencia del tempo, el género
                    y la adecuación a la tarea.
                  </>,
                  <>
                    Toutes les musiques ne conviennent pas&nbsp;: la science du
                    tempo, du genre et de l'adéquation à la tâche
                  </>,
                  <>
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      모든
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      음악이
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      효과적인
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      것은
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      아니다:
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      템포,
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      장르,
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      그리고
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      과제와의
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      연관성에
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      대한
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      과학적
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      분석
                    </span>
                  </>,
                  selectedLanguage
                )}
              </h2>
              <p className={classes.pParagraph}>
                {translate(
                  <>
                    Walk into any office playing random music, and you might
                    actually decrease productivity. The psychology of sound at
                    work reveals that specific musical characteristics determine
                    whether sound helps or hinders performance.
                  </>,
                  <>
                    Si entras en cualquier oficina donde suena música al azar,
                    es probable que disminuya la productividad. La psicología
                    del sonido en el entorno laboral revela que ciertas
                    características musicales específicas determinan si el
                    sonido ayuda o perjudica el rendimiento.
                  </>,
                  <>
                    Entrer dans un bureau où l'on diffuse de la musique
                    aléatoire peut en réalité nuire à la productivité. La
                    psychologie du son au travail révèle que certaines
                    caractéristiques musicales déterminent si la musique
                    favorise ou entrave les performances.
                  </>,
                  <>
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      무작위로
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      음악이
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      흘러나오는
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      사무실에
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      들어가
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      보면
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      실제로
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      생산성이
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      저하될
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      수
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      있습니다.
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      직장에서의
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      소리에
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      대한
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      심리학
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      연구에
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      따르면
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      특정
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      음악적
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      특성이
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      업무
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      성과에
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      도움이
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      되는지
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      방해가
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      되는지를
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      결정합니다.
                    </span>
                  </>,
                  selectedLanguage
                )}
              </p>
              <p className={classes.pParagraph}>
                {translate(
                  <>
                    This is where many{' '}
                    <a
                      className={classes.aHref}
                      target="_blank"
                      href={
                        'https://medium.com/practicalstream/the-secret-soundtrack-formula-for-cafes-and-gyms-business-music-ad6a21716a13'
                      }
                      title={
                        'The Secret Soundtrack Formula: For Cafes and Gyms Business'
                      }
                      rel="nofollow noopener noreferrer"
                    >
                      workplace music initiatives{' '}
                      <span className={classes.spanPre}>
                        fail&nbsp;
                        <IconExternalLink />
                      </span>
                    </a>
                    . Leaders assume “any pleasant music is better than silence”
                    and implement generic playlists without understanding the
                    neuroscience of task-music alignment.
                  </>,
                  <>
                    Aquí es donde fracasan{' '}
                    <a
                      className={classes.aHref}
                      target="_blank"
                      href={
                        'https://medium.com/practicalstream/the-secret-soundtrack-formula-for-cafes-and-gyms-business-music-ad6a21716a13'
                      }
                      title={
                        'La fórmula secreta para la banda sonora perfecta: para cafeterías y gimnasios.'
                      }
                      rel="nofollow noopener noreferrer"
                    >
                      muchas iniciativas musicales en el lugar de{' '}
                      <span className={classes.spanPre}>
                        trabajo&nbsp;
                        <IconExternalLink />
                      </span>
                    </a>
                    . Los líderes asumen que "cualquier música agradable es
                    mejor que el silencio" e implementan listas de reproducción
                    genéricas sin comprender la neurociencia de la relación
                    entre la música y el rendimiento en las tareas.
                  </>,
                  <>
                    C'est là que de nombreuses{' '}
                    <a
                      className={classes.aHref}
                      target="_blank"
                      href={
                        'https://medium.com/practicalstream/the-secret-soundtrack-formula-for-cafes-and-gyms-business-music-ad6a21716a13'
                      }
                      title={
                        'La formule secrète de la bande sonore : pour les cafés et les salles de sport.'
                      }
                      rel="nofollow noopener noreferrer"
                    >
                      initiatives musicales en milieu de travail{' '}
                      <span className={classes.spanPre}>
                        échouent&nbsp;
                        <IconExternalLink />
                      </span>
                    </a>
                    . Les dirigeants partent du principe que «&nbsp;toute
                    musique agréable vaut mieux que le silence&nbsp;» et mettent
                    en place des listes de lecture génériques sans comprendre
                    les fondements neuroscientifiques de l'adéquation entre la
                    musique et la tâche à accomplir.
                  </>,
                  <>
                    <a
                      className={classes.aHref}
                      target="_blank"
                      href={
                        'https://medium.com/practicalstream/the-secret-soundtrack-formula-for-cafes-and-gyms-business-music-ad6a21716a13'
                      }
                      title={
                        '비밀스러운 배경음악 공식: 카페 및 헬스장 사업을 위한 성공 전략'
                      }
                      rel="nofollow noopener noreferrer"
                    >
                      {' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        많은
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        직장
                      </span>{' '}
                      <span className={classes.spanPre}>
                        음악&nbsp;
                        <IconExternalLink />
                      </span>
                    </a>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      도입
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      시도가
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      실패하는
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      지점이
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      바로
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      여기입니다.
                    </span>{' '}
                    <span
                      aria-hidden={true}
                      className={classes.asianTextBlock}
                    ></span>
                    <br />
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      리더들은
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      "아무런
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      침묵보다는
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      기분
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      좋은
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      음악이라도
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      낫다"라고
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      생각하며,
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      업무와
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      음악의
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      연관성에
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      대한
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      신경과학적
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      원리를
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      이해하지
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      못한
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      채
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      일반적인
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      플레이리스트를
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      도입합니다.
                    </span>
                  </>,
                  selectedLanguage
                )}
              </p>
            </section>
            <section id="the-business-music-formula">
              <h2 className={classes.subSection}>
                {translate(
                  <>The Tempo Formula: Matching BPM to Brain States</>,
                  <>
                    La fórmula del tempo: Sincronizando los BPM con los estados
                    cerebrales
                  </>,
                  <>
                    La formule du tempo&nbsp;: adapter le BPM aux états
                    cérébraux
                  </>,
                  <>
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      템포
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      공식:
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      BPM을
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      뇌
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      상태에
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      맞춰라
                    </span>
                  </>,
                  selectedLanguage
                )}
              </h2>
              <p className={classes.pParagraph}>
                {translate(
                  <>
                    The correlation between music and productivity at work
                    follows predictable patterns based on beats per minute
                    (BPM). Your brain naturally synchronizes with musical tempo
                    through a phenomenon called neural entrainment&nbsp;— your
                    brain waves literally align with rhythmic external stimuli.
                  </>,
                  <>
                    La correlación entre la música y la productividad en el
                    trabajo sigue patrones predecibles basados ​​en las
                    pulsaciones por minuto (BPM). El cerebro se sincroniza de
                    forma natural con el tempo musical a través de un fenómeno
                    llamado arrastre neuronal: las ondas cerebrales se alinean
                    literalmente con los estímulos rítmicos externos.
                  </>,
                  <>
                    La corrélation entre la musique et la productivité au
                    travail suit des schémas prévisibles basés sur le nombre de
                    battements par minute. Votre cerveau se synchronise
                    naturellement avec le tempo musical grâce à un phénomène
                    appelé entraînement neuronal&nbsp;: vos ondes cérébrales
                    s'alignent littéralement sur les stimuli rythmiques
                    externes.
                  </>,
                  <>
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      음악과
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      업무
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      생산성
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      사이의
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      상관관계는
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      분당
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      비트
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      수(BPM)에
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      따라
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      예측
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      가능한
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      패턴을
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      따릅니다.
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      우리의
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      뇌는
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      신경
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      동조화라는
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      현상을
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      통해
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      음악적
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      템포에
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      자연스럽게
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      동기화됩니다.
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      즉,
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      뇌파가
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      리듬감
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      있는
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      외부
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      자극에
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      맞춰
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      정렬되는
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      것입니다.
                    </span>
                  </>,
                  selectedLanguage
                )}
              </p>
              <section id="tempo-range-for-deep-focus">
                <h3 className={classes.pParagraph}>
                  {translate(
                    <>
                      <span className={classes.spanBolder}>
                        1.&nbsp;60–70 BPM: Deep Focus and Analytical Work
                      </span>
                    </>,
                    <>
                      <span className={classes.spanBolder}>
                        1.&nbsp;60-70 pulsaciones por minuto: Concentración
                        profunda y trabajo analítico.
                      </span>
                    </>,
                    <>
                      <span className={classes.spanBolder}>
                        1.&nbsp;60 à 70 battements par minute&nbsp;:
                        Concentration intense et travail analytique.
                      </span>
                    </>,
                    <>
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        1.
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        60~70
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        BPM:
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        깊은
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        집중력과
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        분석적인
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        작업에
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        적합합니다.
                      </span>
                    </>,
                    selectedLanguage
                  )}
                </h3>
                <p className={classes.pParagraph}>
                  {translate(
                    <>
                      This tempo range mirrors your resting heart rate,
                      promoting calm alertness without drowsiness. Music in this
                      range activates Alpha brain waves associated with relaxed
                      concentration.
                    </>,
                    <>
                      Este rango de tempo refleja tu frecuencia cardíaca en
                      reposo, lo que favorece un estado de alerta tranquilo sin
                      provocar somnolencia. La música en este rango activa las
                      ondas cerebrales alfa, asociadas con la concentración
                      relajada.
                    </>,
                    <>
                      Cette plage de tempo correspond à votre fréquence
                      cardiaque au repos, favorisant un état de calme et de
                      vigilance sans somnolence. La musique dans cette gamme
                      active les ondes cérébrales alpha, associées à une
                      concentration détendue.
                    </>,
                    <>
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        이러한
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        템포
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        범위는
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        안정시
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        심박수와
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        유사하여
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        졸음
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        없이
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        차분한
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        집중력을
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        유지하도록
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        도와줍니다.
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        이
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        범위의
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        음악은
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        편안한
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        집중력과
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        관련된
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        알파
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        뇌파를
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        활성화합니다.
                      </span>
                    </>,
                    selectedLanguage
                  )}
                </p>
                <p className={classes.pParagraph}>
                  {translate(
                    <>
                      <span className={classes.spanBolder}>
                        Ideal for:&nbsp;
                      </span>
                      Report writing, data analysis, coding, strategic planning,
                      financial modeling, legal document review
                    </>,
                    <>
                      <span className={classes.spanBolder}>
                        Ideal para:&nbsp;
                      </span>
                      El ritmo pausado evita la sobreestimulación, a la vez que
                      mantiene la atención suficiente para enmascarar los
                      sonidos ambientales que puedan resultar distractores.
                    </>,
                    <>
                      <span className={classes.spanBolder}>
                        Idéal pour&nbsp;:&nbsp;
                      </span>
                      Rédaction de rapports, analyse de données, programmation,
                      planification stratégique, modélisation financière,
                      révision de documents juridiques
                    </>,
                    <>
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        다음과
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        같은
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        작업에
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        이상적입니다:
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        보고서
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        작성,
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        데이터
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        분석,
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        코딩,
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        전략
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        기획, 재무
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        모델링,
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        법률
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        문서
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        검토
                      </span>
                    </>,
                    selectedLanguage
                  )}
                </p>
                <p className={classes.pParagraph}>
                  {translate(
                    <>
                      <span className={classes.spanBolder}>
                        Why it works:&nbsp;
                      </span>
                      The slow tempo prevents overstimulation while maintaining
                      enough engagement to mask distracting environmental
                      sounds.
                    </>,
                    <>
                      <span className={classes.spanBolder}>
                        Por qué funciona:&nbsp;
                      </span>
                      El ritmo pausado evita la sobreestimulación, a la vez que
                      mantiene la atención suficiente para enmascarar los
                      sonidos ambientales que puedan resultar distractores.
                    </>,
                    <>
                      <span className={classes.spanBolder}>
                        Pourquoi ça marche&nbsp;:&nbsp;
                      </span>
                      Le rythme lent prévient la surstimulation tout en
                      maintenant un niveau d'attention suffisant pour masquer
                      les bruits ambiants gênants.
                    </>,
                    <>
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        효과적인
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        이유:
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        느린
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        템포는
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        과도한
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        자극을
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        방지하는
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        동시에
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        주변의
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        방해되는
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        소음을
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        차단할
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        만큼
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        충분한
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        집중력을
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        유지시켜
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        줍니다.
                      </span>
                    </>,
                    selectedLanguage
                  )}
                </p>
              </section>
              <section id="tempo-for-moderate-focus">
                <h3 className={classes.pParagraph}>
                  {translate(
                    <>
                      <span className={classes.spanBolder}>
                        2.&nbsp;70–100 BPM: Moderate Concentration Task
                      </span>
                    </>,
                    <>
                      <span className={classes.spanBolder}>
                        2.&nbsp;70–100 pulsaciones por minuto: Tarea que
                        requiere concentración moderada.
                      </span>
                    </>,
                    <>
                      <span className={classes.spanBolder}>
                        2.&nbsp;70 à 100 impulsions par minute&nbsp;: Tarea que
                        requiere concentration modérée.
                      </span>
                    </>,
                    <>
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        2.
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        70~100
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        BPM:
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        중간
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        정도의
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        집중력이
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        필요한
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        작업
                      </span>
                    </>,
                    selectedLanguage
                  )}
                </h3>
                <p className={classes.pParagraph}>
                  {translate(
                    <>
                      This mid-range tempo balances mental energy with sustained
                      attention. It’s engaging enough to maintain focus without
                      being so stimulating that it becomes distracting.
                    </>,
                    <>
                      Este ritmo moderado equilibra la energía mental con la
                      atención sostenida. Es lo suficientemente atractivo como
                      para mantener la concentración sin ser tan estimulante que
                      llegue a distraer.
                    </>,
                    <>
                      Ce tempo modéré permet d'équilibrer l'énergie mentale et
                      l'attention soutenue. Il est suffisamment stimulant pour
                      maintenir la concentration sans pour autant être trop
                      intense au point de devenir une source de distraction.
                    </>,
                    <>
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        이
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        중간
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        속도의
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        템포는
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        정신적
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        에너지와
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        지속적인
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        집중력
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        사이의
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        균형을
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        맞춰줍니다.
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      ></span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        집중력을
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        유지할
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        만큼
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        충분히
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        흥미로우면서도,
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        주의를
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        산만하게
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        할
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        정도로
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        자극적이지는
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        않습니다.
                      </span>
                    </>,
                    selectedLanguage
                  )}
                </p>
                <p className={classes.pParagraph}>
                  {translate(
                    <>
                      <span className={classes.spanBolder}>
                        Ideal for:&nbsp;
                      </span>
                      Email management, research, routine administrative work,
                      customer service documentation, basic design tasks
                    </>,
                    <>
                      <span className={classes.spanBolder}>
                        Ideal para:&nbsp;
                      </span>
                      Gestión de correo electrónico, investigación, trabajo
                      administrativo de rutina, documentación de atención al
                      cliente, tareas básicas de diseño.
                    </>,
                    <>
                      <span className={classes.spanBolder}>
                        Idéal pour&nbsp;:&nbsp;
                      </span>
                      Gestion des e-mails, recherche, tâches administratives
                      courantes, documentation du service client, tâches de
                      conception de base
                    </>,
                    <>
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        다음과
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        같은
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        작업에
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        적합합니다:
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        이메일
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        관리,
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        자료
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        조사,
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        일상적인
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        행정
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        업무, 고객
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        서비스
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        문서
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        작성,
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        기본적인
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        디자인
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        작업
                      </span>
                    </>,
                    selectedLanguage
                  )}
                </p>
                <p className={classes.pParagraph}>
                  {translate(
                    <>
                      <span className={classes.spanBolder}>
                        Why it works:&nbsp;
                      </span>
                      Matches the natural rhythm of moderately-paced work,
                      providing gentle motivation without overwhelming cognitive
                      resources.
                    </>,
                    <>
                      <span className={classes.spanBolder}>
                        Por qué funciona:&nbsp;
                      </span>
                      Se adapta al ritmo natural del trabajo a un ritmo
                      moderado, proporcionando una motivación suave sin
                      sobrecargar los recursos cognitivos.
                    </>,
                    <>
                      <span className={classes.spanBolder}>
                        Pourquoi ça marche&nbsp;:&nbsp;
                      </span>
                      Cela correspond au rythme naturel d'un travail à un rythme
                      modéré, offrant une motivation douce sans surcharger les
                      ressources cognitives.
                    </>,
                    <>
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        효과적인
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        이유:
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        적당한
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        속도로
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        진행되는
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        업무의
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        자연스러운
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        리듬과
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        일치하여
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        인지
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        자원을
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        과도하게
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        소모하지
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        않으면서
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        부드러운
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        동기
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        부여를
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        제공합니다.
                      </span>
                    </>,
                    selectedLanguage
                  )}
                </p>
              </section>
              <section id="tempo-for-creativity">
                <h3 className={classes.pParagraph}>
                  {translate(
                    <>
                      <span className={classes.spanBolder}>
                        3.&nbsp;100–140 BPM: Creative Work and Collaboration
                      </span>
                    </>,
                    <>
                      <span className={classes.spanBolder}>
                        3.&nbsp;100-140 pulsaciones por minuto: Trabajo creativo
                        y colaboración.
                      </span>
                    </>,
                    <>
                      <span className={classes.spanBolder}>
                        3.&nbsp;100–140 BPM&nbsp;: Travail créatif et
                        collaboration
                      </span>
                    </>,
                    <>
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        3.
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        100~140
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        BPM:
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        창의적인
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        작업
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        및
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        협업
                      </span>
                    </>,
                    selectedLanguage
                  )}
                </h3>
                <p className={classes.pParagraph}>
                  {translate(
                    <>
                      Faster tempos increase arousal and energy levels, making
                      them perfect for collaborative activities and creative
                      brainstorming. This range stimulates Beta brain waves
                      associated with active thinking and engagement.
                    </>,
                    <>
                      Los ritmos más rápidos aumentan la excitación y los
                      niveles de energía, lo que los hace perfectos para
                      actividades colaborativas y sesiones de lluvia de ideas
                      creativas. Este rango estimula las ondas cerebrales beta,
                      asociadas con el pensamiento activo y la concentración.
                    </>,
                    <>
                      Des tempos plus rapides augmentent l'excitation et le
                      niveau d'énergie, ce qui les rend idéaux pour les
                      activités collaboratives et le brainstorming créatif.
                      Cette gamme de tempos stimule les ondes cérébrales bêta
                      associées à la pensée active et à l'engagement.
                    </>,
                    <>
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        빠른
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        템포는
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        각성도와
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        에너지
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        수준을
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        높여
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        협업
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        활동이나
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        창의적인
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        브레인스토밍에
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        이상적입니다.
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        이러한
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        템포
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        범위는
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        활발한
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        사고
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        및
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        참여와
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        관련된
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        베타
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        뇌파를
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        자극합니다.
                      </span>
                    </>,
                    selectedLanguage
                  )}
                </p>
                <p className={classes.pParagraph}>
                  {translate(
                    <>
                      <span className={classes.spanBolder}>
                        Ideal for:&nbsp;
                      </span>
                      Brainstorming sessions, design work, team projects,
                      problem-solving workshops, creative writing, marketing
                      content creation.
                    </>,
                    <>
                      <span className={classes.spanBolder}>
                        Ideal para:&nbsp;
                      </span>
                      Sesiones de lluvia de ideas, trabajo de diseño, proyectos
                      en equipo, talleres de resolución de problemas, escritura
                      creativa, creación de contenido de marketing.
                    </>,
                    <>
                      <span className={classes.spanBolder}>
                        Idéal pour&nbsp;:&nbsp;
                      </span>
                      Séances de brainstorming, travail de conception, projets
                      d'équipe, ateliers de résolution de problèmes, écriture
                      créative, création de contenu marketing
                    </>,
                    <>
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        다음과
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        같은
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        활동에
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        이상적입니다:
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        브레인스토밍
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        회의,
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        디자인
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        작업,
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        팀
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        프로젝트,
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        문제
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        해결
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        워크숍,
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        창작
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        글쓰기,
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        마케팅
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        콘텐츠
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        제작.
                      </span>
                    </>,
                    selectedLanguage
                  )}
                </p>
                <p className={classes.pParagraph}>
                  {translate(
                    <>
                      <span className={classes.spanBolder}>
                        Why it works:&nbsp;
                      </span>
                      The energizing tempo promotes mental flexibility,
                      divergent thinking, and social engagement&nbsp;— all
                      crucial for creative and collaborative work.
                    </>,
                    <>
                      <span className={classes.spanBolder}>
                        Por qué funciona:&nbsp;
                      </span>
                      El ritmo energizante fomenta la flexibilidad mental, el
                      pensamiento divergente y la interacción social, elementos
                      cruciales para el trabajo creativo y colaborativo.
                    </>,
                    <>
                      <span className={classes.spanBolder}>
                        Pourquoi ça marche&nbsp;:&nbsp;
                      </span>
                      Ce rythme énergique favorise la flexibilité mentale, la
                      pensée divergente et l'interaction sociale, autant
                      d'éléments cruciaux pour le travail créatif et
                      collaboratif.
                    </>,
                    <>
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        효과적인
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        이유:
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        활기찬
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        템포는
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        정신적
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        유연성,
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        확산적
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        사고,
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        그리고
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        사회적
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        참여를
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        촉진합니다.
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        이
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        모든
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        요소는
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        창의적이고
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        협업적인
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        업무에
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        매우
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        중요합니다.
                      </span>
                    </>,
                    selectedLanguage
                  )}
                </p>
              </section>
              <section id="tempo-for-physical-work">
                <h3 className={classes.pParagraph}>
                  {translate(
                    <>
                      <span className={classes.spanBolder}>
                        4.&nbsp;140+ BPM: Physical Tasks and Repetitive Work
                      </span>
                    </>,
                    <>
                      <span className={classes.spanBolder}>
                        4.&nbsp;Más de 140 pulsaciones por minuto: Tareas
                        físicas y trabajo repetitivo
                      </span>
                    </>,
                    <>
                      <span className={classes.spanBolder}>
                        4.&nbsp;Plus de 140 battements par minute&nbsp;: Tâches
                        physiques et travail répétitif
                      </span>
                    </>,
                    <>
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        4.
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        140BPM
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        이상:
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        신체
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        활동
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        및
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        반복적인
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        작업
                      </span>
                    </>,
                    selectedLanguage
                  )}
                </h3>
                <p className={classes.pParagraph}>
                  {translate(
                    <>
                      High-tempo music drives action and maintains pace for
                      physical or highly repetitive cognitive tasks. The fast
                      rhythm prevents mental fatigue during monotonous
                      activities.
                    </>,
                    <>
                      La música con un ritmo rápido estimula la actividad y
                      mantiene el ritmo en tareas físicas o cognitivas altamente
                      repetitivas. El ritmo acelerado previene la fatiga mental
                      durante actividades monótonas.
                    </>,
                    <>
                      La musique rythmée stimule l'action et maintient le rythme
                      lors de tâches physiques ou cognitives très répétitives.
                      Le rythme rapide prévient la fatigue mentale pendant les
                      activités monotones.
                    </>,
                    <>
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        빠른
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        템포의
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        음악은
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        활동성을
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        높이고
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        신체
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        활동이나
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        반복적인
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        인지
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        작업을
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        수행하는
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        동안
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        집중력을
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        유지하는
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        데
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        도움이
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        됩니다.
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        빠른
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        리듬은
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        단조로운
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        활동
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        중
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        정신적
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        피로를
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        예방해
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        줍니다.
                      </span>
                    </>,
                    selectedLanguage
                  )}
                </p>
                <p className={classes.pParagraph}>
                  {translate(
                    <>
                      <span className={classes.spanBolder}>
                        Ideal for:&nbsp;
                      </span>
                      Data entry, filing, assembly work, warehouse operations,
                      inventory management, routine quality checks.
                    </>,
                    <>
                      <span className={classes.spanBolder}>
                        Ideal para:&nbsp;
                      </span>
                      Entrada de datos, archivo, trabajos de montaje,
                      operaciones de almacén, gestión de inventario, controles
                      de calidad rutinarios.
                    </>,
                    <>
                      <span className={classes.spanBolder}>
                        Idéal pour&nbsp;:&nbsp;
                      </span>
                      Saisie de données, classement, travaux d'assemblage,
                      opérations d'entrepôt, gestion des stocks, contrôles
                      qualité de routine.
                    </>,
                    <>
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        데이터
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        입력,
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        서류
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        정리,
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        조립
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        작업,
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        창고
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        운영, 재고
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        관리,
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        정기
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        품질
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        검사.
                      </span>
                    </>,
                    selectedLanguage
                  )}
                </p>
                <p className={classes.pParagraph}>
                  {translate(
                    <>
                      <span className={classes.spanBolder}>
                        Why it works:&nbsp;
                      </span>
                      The driving beat maintains energy and prevents the mind
                      from wandering during repetitive tasks that might
                      otherwise feel tedious.
                    </>,
                    <>
                      <span className={classes.spanBolder}>
                        Por qué funciona:&nbsp;
                      </span>
                      El ritmo constante mantiene los niveles de energía y evita
                      que la mente divague durante las tareas repetitivas que,
                      de otro modo, podrían resultar tediosas.
                    </>,
                    <>
                      <span className={classes.spanBolder}>
                        Pourquoi ça marche&nbsp;:&nbsp;
                      </span>
                      Le rythme entraînant maintient l'énergie et empêche
                      l'esprit de divaguer pendant les tâches répétitives qui
                      pourraient autrement paraître fastidieuses.
                    </>,
                    <>
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        효과적인
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        이유:
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        경쾌한
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        비트가
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        에너지를
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        유지시켜
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        주고,
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        그렇지
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        않으면
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        지루하게
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        느껴질
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        수
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        있는
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        반복적인
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        작업
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        중에
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        마음이
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        산만해지는
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        것을
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        방지해
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        줍니다.
                      </span>
                    </>,
                    selectedLanguage
                  )}
                </p>
              </section>
            </section>
            <section id="music-genre-and-brain-function">
              <h2 className={classes.subSection}>
                {translate(
                  <>
                    Genre Selection: Why Musical Style Impacts Brain Function
                  </>,
                  <>
                    Selección de género musical: Por qué el estilo musical
                    influye en la función cerebral
                  </>,
                  <>
                    Sélection du genre musical&nbsp;: pourquoi le style musical
                    influence le fonctionnement du cerveau.
                  </>,
                  <>
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      장르
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      선택:
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      음악
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      스타일이
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      뇌
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      기능에
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      영향을
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      미치는
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      이유
                    </span>
                  </>,
                  selectedLanguage
                )}
              </h2>
              <p className={classes.pParagraph}>
                {translate(
                  <>
                    Beyond tempo, musical genre engages different neural systems
                    and triggers distinct cognitive effects.
                  </>,
                  <>
                    Más allá del tempo, el género musical activa diferentes
                    sistemas neuronales y desencadena efectos cognitivos
                    distintos.
                  </>,
                  <>
                    Au-delà du tempo, le genre musical sollicite différents
                    systèmes neuronaux et déclenche des effets cognitifs
                    distincts.
                  </>,
                  <>
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      템포
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      외에도
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      음악
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      장르는
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      다양한
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      신경계를
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      활성화시키고
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      서로
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      다른
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      인지적
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      효과를
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      유발합니다.
                    </span>
                  </>,
                  selectedLanguage
                )}
              </p>
              <section id="classical-and-brain-function">
                <h3 className={classes.subSection}>
                  {translate(
                    <>Classical and Orchestral Instrumental</>,
                    <>Instrumental clásica y orquestal</>,
                    <>Musique classique et instrumentale orchestrale</>,
                    <>
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        클래식
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        및
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        오케스트라
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        기악곡
                      </span>
                    </>,
                    selectedLanguage
                  )}
                </h3>
                <p className={classes.pParagraph}>
                  {translate(
                    <>
                      Research consistently demonstrates that classical music
                      enhances cognitive performance without linguistic
                      interference. One study found that classical music
                      significantly increased working memory performance
                      compared to both silence and other genres.
                    </>,
                    <>
                      Diversas investigaciones demuestran consistentemente que
                      la música clásica mejora el rendimiento cognitivo sin
                      interferencias lingüísticas. Un estudio reveló que la
                      música clásica incrementó significativamente el
                      rendimiento de la memoria de trabajo en comparación con el
                      silencio y otros géneros musicales.
                    </>,
                    <>
                      Les recherches démontrent de manière constante que la
                      musique classique améliore les performances cognitives
                      sans interférer avec les capacités linguistiques. Une
                      étude a révélé que la musique classique augmentait
                      significativement les performances de la mémoire de
                      travail par rapport au silence et aux autres genres
                      musicaux.
                    </>,
                    <>
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        연구
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        결과는
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        고전
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        음악이
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        언어적
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        간섭
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        없이
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        인지
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        능력을
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        향상시킨다는
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        것을
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        일관되게
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        보여줍니다.
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        한
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        연구에서는
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        고전
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        음악이
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        침묵이나
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        다른
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        장르의
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        음악에
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        비해
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        작업
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        기억력
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        수행
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        능력을
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        현저히
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        향상시키는
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        것으로
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        나타났습니다.
                      </span>
                    </>,
                    selectedLanguage
                  )}
                </p>
                <p className={classes.pParagraph}>
                  {translate(
                    <>
                      The complexity of classical compositions engages multiple
                      brain regions simultaneously without overwhelming
                      cognitive resources. The lack of lyrics prevents language
                      processing conflicts during verbal tasks.
                    </>,
                    <>
                      La complejidad de las composiciones clásicas activa
                      simultáneamente múltiples regiones cerebrales sin
                      sobrecargar los recursos cognitivos. La ausencia de letra
                      evita conflictos en el procesamiento del lenguaje durante
                      las tareas verbales.
                    </>,
                    <>
                      La complexité des compositions classiques sollicite
                      simultanément plusieurs régions du cerveau sans pour
                      autant surcharger les ressources cognitives. L'absence de
                      paroles évite les interférences liées au traitement du
                      langage lors des tâches verbales.
                    </>,
                    <>
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        클래식
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        음악의
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        복잡성은
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        인지
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        자원을
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        과부하하지
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        않으면서
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        여러
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        뇌
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        영역을
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        동시에
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        활성화합니다.
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        가사가
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        없기
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        때문에
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        언어
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        처리
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        과정에서
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        발생하는
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        충돌
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        없이
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        언어
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        관련
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        작업을
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        수행할
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        수
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        있습니다.
                      </span>
                    </>,
                    selectedLanguage
                  )}
                </p>
                <p className={classes.pParagraph}>
                  {translate(
                    <>
                      <span className={classes.spanItalic}>Best&nbsp;for:</span>{' '}
                      Writing, reading, learning, analytical thinking
                    </>,
                    <>
                      <span className={classes.spanItalic}>
                        Ideal&nbsp;para:
                      </span>{' '}
                      Escribir, leer, aprender, pensamiento analítico
                    </>,
                    <>
                      <span className={classes.spanItalic}>
                        Idéal pour&nbsp;:
                      </span>{' '}
                      Écriture, lecture, apprentissage, pensée analytique
                    </>,
                    <>
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        가장
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        적합한
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        용도:
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        글쓰기,
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        독서,
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        학습,
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        분석적
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        사고
                      </span>
                    </>,
                    selectedLanguage
                  )}
                </p>
              </section>
              <section id="ambient-nature-and-brain-function">
                <h3 className={classes.subSection}>
                  {translate(
                    <>Ambient and Nature Sounds</>,
                    <>Sonidos ambientales y de la naturaleza</>,
                    <>Sons d'ambiance et de nature</>,
                    <>
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        주변
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        소음
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        및
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        자연
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        소리
                      </span>
                    </>,
                    selectedLanguage
                  )}
                </h3>
                <p className={classes.pParagraph}>
                  {translate(
                    <>
                      Ambient music and nature soundscapes (rainfall, ocean
                      waves, forest sounds) create optimal background noise
                      through a phenomenon called stochastic resonance. Research
                      from Rensselaer Polytechnic Institute found that moderate
                      ambient noise (around 70 decibels&nbsp;— think coffee shop
                      level) actually enhances creative cognition.
                    </>,
                    <>
                      La música ambiental y los sonidos de la naturaleza
                      (lluvia, olas del mar, sonidos del bosque) crean un ruido
                      de fondo óptimo gracias a un fenómeno llamado resonancia
                      estocástica. Investigaciones del Instituto Politécnico
                      Rensselaer descubrieron que el ruido ambiental moderado
                      (alrededor de 70 decibelios, similar al de una cafetería)
                      mejora la capacidad cognitiva y la creatividad.
                    </>,
                    <>
                      La musique d'ambiance et les paysages sonores naturels
                      (pluie, vagues de l'océan, sons de la forêt) créent un
                      bruit de fond optimal grâce à un phénomène appelé
                      résonance stochastique. Des recherches menées par le
                      Rensselaer Polytechnic Institute ont montré qu'un bruit
                      ambiant modéré (environ 70 décibels&nbsp;– l'équivalent du
                      niveau sonore d'un café) améliore les capacités cognitives
                      et la créativité.
                    </>,
                    <>
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        앰비언트
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        음악과
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        자연의
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        소리(빗소리,
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        파도
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        소리,
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        숲
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        소리
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        등)는
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        확률적
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        공명이라는
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        현상을
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        통해
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        최적의
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        배경
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        소음을
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        만들어냅니다.
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        렌셀러
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        폴리테크닉
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        연구소의
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        연구에
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        따르면
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        적당한
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        수준의
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        주변
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        소음(약
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        70데시벨,
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        카페
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        소음
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        정도)은
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        오히려
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        창의적
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        인지
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        능력을
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        향상시키는
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        것으로
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        나타났습니다.
                      </span>
                    </>,
                    selectedLanguage
                  )}
                </p>
                <p className={classes.pParagraph}>
                  {translate(
                    <>
                      The subtle, non-repeating patterns provide enough auditory
                      stimulation to mask distractions without creating their
                      own distraction.
                    </>,
                    <>
                      Los patrones sutiles y no repetitivos proporcionan
                      suficiente estimulación auditiva para enmascarar las
                      distracciones sin crear nuevas distracciones.
                    </>,
                    <>
                      Ces motifs subtils et non répétitifs offrent une
                      stimulation auditive suffisante pour masquer les
                      distractions sans pour autant en créer de nouvelles.
                    </>,
                    <>
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        미묘하고
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        반복되지
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        않는
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        패턴은
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        주의를
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        산만하게
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        하는
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        요소를
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        차단하기에
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        충분한
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        청각적
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        자극을
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        제공하면서도,
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        그
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        자체로
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        새로운
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        방해
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        요소가
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        되지
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        않습니다.
                      </span>
                    </>,
                    selectedLanguage
                  )}
                </p>
                <p className={classes.pParagraph}>
                  {translate(
                    <>
                      <span className={classes.spanItalic}>Best&nbsp;for:</span>{' '}
                      Creative work, meditation, stress reduction, open-ended
                      problem-solving.
                    </>,
                    <>
                      <span className={classes.spanItalic}>Ideal para:</span>{' '}
                      Trabajo creativo, meditación, reducción del estrés,
                      resolución de problemas de forma abierta.
                    </>,
                    <>
                      <span className={classes.spanItalic}>
                        Idéal pour&nbsp;:
                      </span>{' '}
                      Travail créatif, méditation, réduction du stress,
                      résolution de problèmes complexes.
                    </>,
                    <>
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        추천
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        용도:
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        창의적인
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        작업,
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        명상,
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        스트레스
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        해소,
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        개방형
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        문제
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        해결.
                      </span>
                    </>,
                    selectedLanguage
                  )}
                </p>
              </section>
              <section id="lo-fi-hip-hop-and-chill-jazz">
                <h3 className={classes.subSection}>
                  {translate(
                    <>Lo-fi Hip Hop and Chill Jazz</>,
                    <>Hip Hop lo-fi y Jazz relajante</>,
                    <>Hip-hop lo-fi et jazz relaxant</>,
                    <>
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        로파이
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        힙합과
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        칠
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        재즈
                      </span>
                    </>,
                    selectedLanguage
                  )}
                </h3>
                <p className={classes.pParagraph}>
                  {translate(
                    <>
                      These genres have exploded in popularity among knowledge
                      workers for good reason. They offer the sweet spot many
                      professionals seek: engaging enough to mask environmental
                      distractions, predictable enough to fade into background,
                      and musically interesting enough to remain pleasant during
                      long work sessions.
                    </>,
                    <>
                      Estos géneros musicales han ganado una enorme popularidad
                      entre los trabajadores del conocimiento, y con razón.
                      Ofrecen la combinación perfecta que muchos profesionales
                      buscan: lo suficientemente atractivos como para disimular
                      las distracciones del entorno, lo suficientemente
                      predecibles como para pasar a un segundo plano y lo
                      suficientemente interesantes musicalmente como para
                      resultar agradables durante largas jornadas de trabajo.
                    </>,
                    <>
                      Ces genres musicaux ont connu un succès fulgurant auprès
                      des travailleurs du savoir, et ce pour de bonnes raisons.
                      Ils offrent le juste équilibre que recherchent de nombreux
                      professionnels&nbsp;: suffisamment captivants pour masquer
                      les distractions environnantes, suffisamment prévisibles
                      pour se fondre dans l'arrière-plan et suffisamment
                      intéressants musicalement pour rester agréables pendant de
                      longues séances de travail.
                    </>,
                    <>
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        이러한
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        장르들은
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        지식
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        노동자들
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        사이에서
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        엄청난
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        인기를
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        얻고
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        있는데,
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        그럴
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        만한
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        이유가
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        있습니다.
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        이
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        장르들은
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        많은
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        전문가들이
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        찾는
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        이상적인
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        조건을
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        제공합니다.
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        주변
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        환경의
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        방해
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        요소를
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        가릴
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        만큼
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        몰입감을
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        주면서도,
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        배경음악처럼
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        자연스럽게
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        흘러가고,
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        장시간
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        작업
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        중에도
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        지루하지
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        않고
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        즐겁게
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        들을
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        수
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        있을
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        만큼
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        음악적으로
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        흥미롭기
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        때문입니다.
                      </span>
                    </>,
                    selectedLanguage
                  )}
                </p>
                <p className={classes.pParagraph}>
                  {translate(
                    <>
                      The characteristic “dusty” sound quality and repetitive
                      beats create a cocoon-like auditory environment that many
                      find ideal for sustained focus.
                    </>,
                    <>
                      La calidad de sonido característica, con un toque
                      "polvoriento", y los ritmos repetitivos crean un entorno
                      auditivo similar a un capullo que muchos consideran ideal
                      para mantener la concentración durante largos periodos.
                    </>,
                    <>
                      La qualité sonore caractéristique, légèrement étouffée, et
                      les rythmes répétitifs créent un environnement auditif
                      semblable à un cocon, que beaucoup trouvent idéal pour une
                      concentration soutenue.
                    </>,
                    <>
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        특유의
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        "먼지
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        낀
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        듯한"
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        음질과
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        반복적인
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        비트는
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        아늑하고
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        포근한
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        청각적
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        환경을
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        조성하여
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        많은
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        사람들이
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        집중력을
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        유지하는
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        데
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        이상적이라고
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        생각합니다.
                      </span>
                    </>,
                    selectedLanguage
                  )}
                </p>
                <p className={classes.pParagraph}>
                  {translate(
                    <>
                      <span className={classes.spanItalic}>Best&nbsp;for:</span>{' '}
                      Programming, writing, studying, design work, research.
                    </>,
                    <>
                      <span className={classes.spanItalic}>Ideal para:</span>{' '}
                      Programación, escritura, estudio, trabajo de diseño,
                      investigación.
                    </>,
                    <>
                      <span className={classes.spanItalic}>
                        Idéal pour&nbsp;:
                      </span>{' '}
                      Programmation, écriture, études, travail de conception,
                      recherche.
                    </>,
                    <>
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        다음
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        용도에
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        가장
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        적합합니다:
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        프로그래밍,
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        글쓰기,
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        공부,
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        디자인
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        작업,
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        연구.
                      </span>
                    </>,
                    selectedLanguage
                  )}
                </p>
              </section>
            </section>
            <section id="video-game-soundtracks">
              <h2 className={classes.subSection}>
                {translate(
                  <>Video Game Soundtracks</>,
                  <>Bandas sonoras de videojuegos</>,
                  <>Bandes sonores de jeux vidéo</>,
                  <>
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      비디오
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      게임
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      사운드트랙
                    </span>
                  </>,
                  selectedLanguage
                )}
              </h2>
              <p className={classes.pParagraph}>
                {translate(
                  <>
                    Here’s an unconventional but highly effective option: video
                    game music. Composers specifically engineer these
                    soundtracks to keep players engaged during repetitive
                    gameplay without becoming distracting or annoying.
                  </>,
                  <>
                    Aquí tienes una opción poco convencional pero muy efectiva:
                    la música de videojuegos. Los compositores diseñan estas
                    bandas sonoras específicamente para mantener a los jugadores
                    concentrados durante el juego, incluso en tareas
                    repetitivas, sin que resulten molestas ni distraigan.
                  </>,
                  <>
                    Voici une option originale mais très efficace&nbsp;: la
                    musique de jeux vidéo. Ces bandes sonores sont spécialement
                    conçues par des compositeurs pour maintenir l'attention des
                    joueurs pendant les phases de jeu répétitives, sans pour
                    autant devenir distrayantes ou agaçantes.
                  </>,
                  <>
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      여기
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      다소
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      색다르지만
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      매우
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      효과적인
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      방법이
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      있습니다.
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      바로
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      비디오
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      게임
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      음악입니다.
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      작곡가들은
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      이러한
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      사운드트랙을
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      반복적인
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      게임
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      플레이
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      중에도
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      플레이어의
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      집중력을
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      유지시키면서도
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      방해가
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      되거나
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      짜증을
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      유발하지
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      않도록
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      특별히
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      제작합니다.
                    </span>
                  </>,
                  selectedLanguage
                )}
              </p>
              <p className={classes.pParagraph}>
                {translate(
                  <>
                    Game music must maintain interest over hours of repeated
                    listening while never overwhelming the player’s attention.
                    These same qualities make it perfect for focused work.
                  </>,
                  <>
                    La música de los videojuegos debe mantener el interés
                    durante horas de escucha repetida sin llegar a distraer la
                    atención del jugador. Estas mismas cualidades la hacen
                    perfecta para concentrarse en el trabajo.
                  </>,
                  <>
                    La musique de jeu vidéo doit maintenir l'intérêt de
                    l'auditeur pendant des heures d'écoute répétée sans jamais
                    accaparer son attention. Ces mêmes qualités la rendent
                    idéale pour le travail nécessitant une concentration
                    intense.
                  </>,
                  <>
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      게임
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      음악은
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      몇
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      시간
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      동안
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      반복해서
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      들어도
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      흥미를
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      유지하면서도
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      플레이어의
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      집중력을
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      방해하지
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      않아야
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      합니다. 이러한
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      특성
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      덕분에
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      게임
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      음악은
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      집중력을
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      요하는
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      작업에
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      완벽하게
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      어울립니다.
                    </span>
                  </>,
                  selectedLanguage
                )}
              </p>
              <p className={classes.pParagraph}>
                {translate(
                  <>
                    <span className={classes.spanItalic}>Best&nbsp;for:</span>{' '}
                    Programming, data analysis, routine cognitive tasks, long
                    focus sessions.
                  </>,
                  <>
                    <span className={classes.spanItalic}>Ideal para:</span>{' '}
                    Programación, análisis de datos, tareas cognitivas
                    rutinarias, sesiones de concentración prolongadas.
                  </>,
                  <>
                    <span className={classes.spanItalic}>
                      Idéal pour&nbsp;:
                    </span>{' '}
                    Programmation, analyse de données, tâches cognitives de
                    routine, sessions de concentration prolongées.
                  </>,
                  <>
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      가장
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      적합한
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      용도:
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      프로그래밍,
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      데이터
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      분석,
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      일상적인
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      인지
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      작업,
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      장시간
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      집중이
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      필요한
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      작업.
                    </span>
                  </>,
                  selectedLanguage
                )}
              </p>
            </section>
            <section id="lyrics-can-be-problematic">
              <h2 className={classes.subSection}>
                {translate(
                  <>The Lyrics Problem: When Words Work Against Your Brain</>,
                  <>
                    El problema de las letras: cuando las palabras juegan en
                    contra de tu cerebro
                  </>,
                  <>
                    Le problème des paroles&nbsp;: quand les mots se retournent
                    contre votre cerveau
                  </>,
                  <>
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      가사
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      문제:
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      단어가
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      당신의
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      뇌에
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      불리하게
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      작용할
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      때
                    </span>
                  </>,
                  selectedLanguage
                )}
              </h2>
              <p className={classes.pParagraph}>
                {translate(
                  <>
                    Here’s where workplace music strategies typically fail, and
                    the neuroscience is unambiguous: music with lyrics creates
                    severe interference for certain task types.
                  </>,
                  <>
                    Aquí es donde suelen fallar las estrategias de música en el
                    lugar de trabajo, y la neurociencia es inequívoca: la música
                    con letra crea una interferencia grave para ciertos tipos de
                    tareas.
                  </>,
                  <>
                    C'est là que les stratégies d'utilisation de la musique au
                    travail échouent généralement, et les neurosciences sont
                    sans équivoque&nbsp;: la musique avec des paroles crée de
                    fortes interférences pour certains types de tâches.
                  </>,
                  <>
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      여기서
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      직장
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      내
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      음악
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      활용
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      전략이
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      흔히
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      실패하는
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      지점이
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      드러납니다.
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      신경과학
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      연구
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      결과는
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      명확합니다.
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      가사가
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      있는
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      음악은
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      특정
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      유형의
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      업무
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      수행에
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      심각한
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      방해를
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      초래합니다.
                    </span>
                  </>,
                  selectedLanguage
                )}
              </p>
              <p className={classes.pParagraph}>
                {translate(
                  <>
                    A 2017 study published in Applied Cognitive Psychology found
                    that college students performed significantly worse on
                    complex cognitive tasks while listening to music with
                    lyrics. The effect was so pronounced that lyrical music
                    actually performed worse than environmental noise or
                    silence.
                  </>,
                  <>
                    Un estudio de 2017 publicado en Applied Cognitive Psychology
                    reveló que los estudiantes universitarios obtenían
                    resultados significativamente peores en tareas cognitivas
                    complejas mientras escuchaban música con letra. El efecto
                    fue tan pronunciado que la música con letra resultó ser
                    incluso más perjudicial que el ruido ambiental o el
                    silencio.
                  </>,
                  <>
                    Une étude de 2017 publiée dans la revue Applied Cognitive
                    Psychology a révélé que les étudiants obtenaient des
                    résultats nettement moins bons lors de tâches cognitives
                    complexes lorsqu'ils écoutaient de la musique avec des
                    paroles. Cet effet était si marqué que l'écoute de musique
                    avec paroles s'est avérée plus néfaste que le bruit ambiant
                    ou le silence.
                  </>,
                  <>
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      2017년
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      응용
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      인지
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      심리학(Applied
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      Cognitive
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      Psychology)
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      저널에
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      발표된
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      연구에
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      따르면,
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      대학생들은
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      가사가
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      있는
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      음악을
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      들으면서
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      복잡한
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      인지
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      과제를
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      수행할
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      때
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      훨씬
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      더
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      저조한
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      성적을
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      보였습니다.
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      이러한
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      효과는
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      매우
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      두드러져서,
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      가사가
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      있는
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      음악을
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      들을
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      때의
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      성적이
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      주변
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      소음이나
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      무음
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      상태일
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      때보다
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      오히려
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      더
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      나빴습니다.
                    </span>
                  </>,
                  selectedLanguage
                )}
              </p>
              <section id="why-lyrics-cause-problems">
                <h3 className={classes.pParagraph}>
                  {translate(
                    <>
                      <span className={classes.spanBolder}>
                        Why lyrics cause problems:
                      </span>
                    </>,
                    <>
                      <span className={classes.spanBolder}>
                        Por qué las letras de las canciones causan problemas:
                      </span>
                    </>,
                    <>
                      <span className={classes.spanBolder}>
                        Pourquoi les paroles de chansons posent problème&nbsp;:
                      </span>
                    </>,
                    <>
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        가사가
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        문제를
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        일으키는
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        이유:
                      </span>
                    </>,
                    selectedLanguage
                  )}
                </h3>
                <p className={classes.pParagraph}>
                  {translate(
                    <>
                      Your brain’s language processing centers (Broca’s area and
                      Wernicke’s area) cannot efficiently process two streams of
                      verbal information simultaneously. When you’re reading a
                      report while listening to a song with lyrics, your brain
                      attempts to parse both the written words and the sung
                      words using the same neural systems.
                    </>,
                    <>
                      Los centros de procesamiento del lenguaje de tu cerebro
                      (el área de Broca y el área de Wernicke) no pueden
                      procesar de forma eficiente dos flujos de información
                      verbal simultáneamente. Cuando lees un informe mientras
                      escuchas una canción con letra, tu cerebro intenta
                      analizar tanto las palabras escritas como las cantadas
                      utilizando los mismos sistemas neuronales.
                    </>,
                    <>
                      Les centres de traitement du langage de votre cerveau
                      (l'aire de Broca et l'aire de Wernicke) ne peuvent pas
                      traiter efficacement deux flux d'informations verbales
                      simultanément. Lorsque vous lisez un rapport tout en
                      écoutant une chanson avec des paroles, votre cerveau tente
                      d'analyser à la fois les mots écrits et les mots chantés
                      en utilisant les mêmes systèmes neuronaux.
                    </>,
                    <>
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        뇌의
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        언어
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        처리
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        센터(브로카
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        영역과
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        베르니케
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        영역)는
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        두
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        가지
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        언어
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        정보를
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        동시에
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        효율적으로
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        처리할
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        수
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        없습니다.
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        가사가
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        있는
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        노래를
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        들으면서
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        보고서를
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        읽을
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        때,
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        뇌는
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        동일한
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        신경계를
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        사용하여
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        읽는
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        단어와
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        노래
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        가사를
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        모두
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        분석하려고
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        시도합니다.
                      </span>
                    </>,
                    selectedLanguage
                  )}
                </p>
                <p className={classes.pParagraph}>
                  {translate(
                    <>
                      This creates what researchers call “cognitive
                      interference”&nbsp;— the two verbal streams compete for
                      limited language processing resources, degrading
                      performance on both tasks.
                    </>,
                    <>
                      Esto crea lo que los investigadores denominan
                      "interferencia cognitiva": las dos secuencias verbales
                      compiten por los recursos limitados de procesamiento del
                      lenguaje, lo que degrada el rendimiento en ambas tareas.
                    </>,
                    <>
                      Cela crée ce que les chercheurs appellent une
                      «&nbsp;interférence cognitive&nbsp;»&nbsp;: les deux flux
                      verbaux se disputent les ressources limitées de traitement
                      du langage, ce qui dégrade les performances dans les deux
                      tâches.
                    </>,
                    <>
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        이로
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        인해
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        연구자들이
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        "인지적
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        간섭"이라고
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        부르는
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        현상이
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        발생합니다.
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        두
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        가지
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        언어
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        정보
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        흐름이
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        제한된
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        언어
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        처리
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        자원을
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        놓고
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        경쟁하면서
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        두
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        가지
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        작업
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        모두에서
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        성능이
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        저하되는
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        것입니다.
                      </span>
                    </>,
                    selectedLanguage
                  )}
                </p>
              </section>
              <section id="when-lyrics-help-performance">
                <h3 className={classes.pParagraph}>
                  {translate(
                    <>
                      <span className={classes.spanBolder}>
                        When lyrics help performance:
                      </span>
                    </>,
                    <>
                      <span className={classes.spanBolder}>
                        Cuando las letras contribuyen a la interpretación:
                      </span>
                    </>,
                    <>
                      <span className={classes.spanBolder}>
                        Quand les paroles contribuent à la performance&nbsp;:
                      </span>
                    </>,
                    <>
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        가사가
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        공연에
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        도움이
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        될
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        때:
                      </span>
                    </>,
                    selectedLanguage
                  )}
                </h3>
                <ul className={classes.standardText + ' ' + classes.ulArticles}>
                  <li className={classes.liArticle}>
                    {translate(
                      <>
                        Repetitive physical tasks (assembly, packing, warehouse
                        work)
                      </>,
                      <>
                        Tareas físicas repetitivas (montaje, embalaje, trabajo
                        en almacén)
                      </>,
                      <>
                        Tâches physiques répétitives (assemblage, emballage,
                        travail en entrepôt)
                      </>,
                      <>
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          반복적인
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          신체
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          작업(조립,
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          포장,
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          창고
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          작업)
                        </span>
                      </>,
                      selectedLanguage
                    )}
                  </li>
                  <li className={classes.liArticle}>
                    {translate(
                      <>
                        Routine administrative duties without verbal processing
                      </>,
                      <>
                        Tareas administrativas rutinarias sin procesamiento
                        verbal
                      </>,
                      <>
                        Tâches administratives courantes ne nécessitant pas de
                        communication verbale.
                      </>,
                      <>
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          구두
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          처리
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          과정이
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          없는
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          일상적인
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          행정
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          업무
                        </span>
                      </>,
                      selectedLanguage
                    )}
                  </li>
                  <li className={classes.liArticle}>
                    {translate(
                      <>Physical exercise or movement-based work</>,
                      <>Ejercicio físico o trabajo basado en el movimiento</>,
                      <>Exercice physique ou travail basé sur le mouvement</>,
                      <>
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          신체
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          운동
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          또는
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          움직임을
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          기반으로
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          하는
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          활동
                        </span>
                      </>,
                      selectedLanguage
                    )}
                  </li>
                  <li className={classes.liArticle}>
                    {translate(
                      <>
                        Tasks you’ve performed so many times they require
                        minimal conscious attention
                      </>,
                      <>
                        Tareas que has realizado tantas veces que requieren una
                        atención consciente mínima
                      </>,
                      <>
                        Des tâches que vous avez effectuées tellement de fois
                        qu'elles ne nécessitent qu'une attention consciente
                        minimale
                      </>,
                      <>
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          너무나
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          여러
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          번
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          수행해서
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          최소한의
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          의식적인
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          주의력만으로도
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          처리할
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          수
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          있는
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          작업들
                        </span>
                      </>,
                      selectedLanguage
                    )}
                  </li>
                </ul>
              </section>
              <section id="when-lyrics-hurt-performance">
                <h3 className={classes.pParagraph}>
                  {translate(
                    <>
                      <span className={classes.spanBolder}>
                        When lyrics severely harm performance:
                      </span>
                    </>,
                    <>
                      <span className={classes.spanBolder}>
                        Cuando las letras perjudican gravemente la
                        interpretación:
                      </span>
                    </>,
                    <>
                      <span className={classes.spanBolder}>
                        Lorsque les paroles nuisent gravement à la
                        performance&nbsp;:
                      </span>
                    </>,
                    <>
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        가사가
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        공연에
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        심각한
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        해를
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        끼치는
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        경우:
                      </span>
                    </>,
                    selectedLanguage
                  )}
                </h3>
                <ul className={classes.standardText + ' ' + classes.ulArticles}>
                  <li className={classes.liArticle}>
                    {translate(
                      <>
                        Reading comprehension (reports, contracts, research
                        papers)
                      </>,
                      <>
                        Comprensión lectora (informes, contratos, artículos de
                        investigación)
                      </>,
                      <>
                        Compréhension de textes (rapports, contrats, articles de
                        recherche)
                      </>,
                      <>
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          독해력
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          (보고서,
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          계약서,
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          연구
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          논문)
                        </span>
                      </>,
                      selectedLanguage
                    )}
                  </li>
                  <li className={classes.liArticle}>
                    {translate(
                      <>
                        Writing or editing (emails, documents, code, creative
                        content)
                      </>,
                      <>
                        Redacción o edición (correos electrónicos, documentos,
                        código, contenido creativo)
                      </>,
                      <>
                        Rédaction ou modification (courriels, documents, code,
                        contenu créatif)
                      </>,
                      <>
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          Writing
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          or
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          editing
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          (emails,
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          documents,
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          code,
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          creative
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        ></span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        ></span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        ></span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        ></span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        ></span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        ></span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        ></span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        ></span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        ></span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        ></span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        ></span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        ></span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        ></span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        ></span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        ></span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        ></span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        ></span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        ></span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        ></span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          content)
                        </span>
                      </>,
                      selectedLanguage
                    )}
                  </li>
                  <li className={classes.liArticle}>
                    {translate(
                      <>
                        Learning new information (training, onboarding, skill
                        development)
                      </>,
                      <>
                        Aprendizaje de nueva información (formación,
                        incorporación a la empresa, desarrollo de habilidades)
                      </>,
                      <>
                        Acquisition de nouvelles connaissances (formation,
                        intégration, développement des compétences)
                      </>,
                      <>
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          새로운
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          정보
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          학습
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          (교육,
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          신입
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          직원
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          오리엔테이션,
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          기술
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          개발)
                        </span>
                      </>,
                      selectedLanguage
                    )}
                  </li>
                  <li className={classes.liArticle}>
                    {translate(
                      <>Complex problem-solving requiring verbal reasoning</>,
                      <>
                        Resolución de problemas complejos que requieren
                        razonamiento verbal
                      </>,
                      <>
                        Résolution de problèmes complexes nécessitant un
                        raisonnement verbal
                      </>,
                      <>
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          언어적
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          추론을
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          요구하는
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          복잡한
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          문제
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          해결
                        </span>
                      </>,
                      selectedLanguage
                    )}
                  </li>
                  <li className={classes.liArticle}>
                    {translate(
                      <>Analytical work involving language processing</>,
                      <>
                        Trabajo analítico que implica el procesamiento del
                        lenguaje
                      </>,
                      <>
                        Travaux d'analyse impliquant le traitement du langage
                      </>,
                      <>
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          언어
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          처리를
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          포함하는
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          분석
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          작업
                        </span>
                      </>,
                      selectedLanguage
                    )}
                  </li>
                </ul>
              </section>
              <section id="practical-solution-for-lyrics">
                <h3 className={classes.pParagraph}>
                  {translate(
                    <>
                      <span className={classes.spanBolder}>
                        The practical solution:
                      </span>
                    </>,
                    <>
                      <span className={classes.spanBolder}>
                        La solución práctica:
                      </span>
                    </>,
                    <>
                      <span className={classes.spanBolder}>
                        La solution pratique&nbsp;:
                      </span>
                    </>,
                    <>
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        실용적인
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        해결책:
                      </span>
                    </>,
                    selectedLanguage
                  )}
                </h3>
                <p className={classes.pParagraph}>
                  {translate(
                    <>
                      The best music for focus during intensive cognitive work
                      features minimal or no vocals, melodies familiar enough to
                      be comfortable but not so catchy they demand attention,
                      predictable musical patterns without jarring surprises or
                      tempo changes, and instrumental complexity appropriate to
                      task difficulty.
                    </>,
                    <>
                      La mejor música para concentrarse durante un trabajo
                      cognitivo intenso se caracteriza por tener pocas o ninguna
                      voz, melodías lo suficientemente familiares como para
                      resultar agradables, pero no tan pegadizas como para
                      distraer, patrones musicales predecibles sin sorpresas ni
                      cambios bruscos de tempo, y una complejidad instrumental
                      adecuada a la dificultad de la tarea.
                    </>,
                    <>
                      La musique idéale pour favoriser la concentration lors
                      d'un travail cognitif intense se caractérise par une
                      présence minimale, voire nulle, de voix, des mélodies
                      suffisamment familières pour être agréables sans pour
                      autant être trop entraînantes et distrayantes, des
                      structures musicales prévisibles sans surprises
                      déstabilisantes ni changements de tempo brusques, et une
                      complexité instrumentale adaptée à la difficulté de la
                      tâche.
                    </>,
                    <>
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      ></span>
                    </>,
                    selectedLanguage
                  )}
                </p>
                <p className={classes.pParagraph}>
                  {translate(
                    <>
                      Interestingly, vocals in foreign languages you don’t
                      understand cause less interference than native-language
                      lyrics. Your brain doesn’t attempt to parse unfamiliar
                      languages using its language centers, allowing the vocals
                      to function more like instruments.
                    </>,
                    <>
                      Curiosamente, las voces en idiomas extranjeros que no
                      entiendes causan menos interferencia que las letras en tu
                      idioma nativo. Tu cerebro no intenta analizar los idiomas
                      desconocidos utilizando sus centros del lenguaje, lo que
                      permite que las voces funcionen más como instrumentos
                      musicales.
                    </>,
                    <>
                      Il est intéressant de noter que les voix chantées dans des
                      langues étrangères que l'on ne comprend pas provoquent
                      moins de distraction que les paroles dans sa langue
                      maternelle. Le cerveau n'essaie pas d'analyser les langues
                      inconnues à l'aide de ses centres du langage, ce qui
                      permet aux voix de fonctionner davantage comme des
                      instruments de musique.
                    </>,
                    <>
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        집중적인
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        인지
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        작업을
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        할
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        때
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        가장
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        적합한
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        음악은
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        다음과
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        같은
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        특징을
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        가지고
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        있습니다.
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        보컬이
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        거의
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        없거나
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        전혀
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        없으며,
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        편안함을
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        줄
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        만큼
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        익숙하지만
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        주의를
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        산만하게
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        할
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        정도로
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        귀에
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        쏙
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        들어오지는
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        않는
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        멜로디,
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        갑작스러운
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        변화나
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        템포
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        변화
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        없이
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        예측
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        가능한
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        음악적
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        패턴,
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        그리고
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        작업
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        난이도에
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        적합한
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        악기
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        구성의
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        복잡성입니다.
                      </span>
                    </>,
                    selectedLanguage
                  )}
                </p>
              </section>
            </section>
            <section id="mood-productivity-connection">
              <h2 className={classes.subSection}>
                {translate(
                  <>The Mood-Productivity Connection Leaders Overlook</>,
                  <>
                    La conexión entre el estado de ánimo y la productividad que
                    los líderes pasan por alto
                  </>,
                  <>
                    Le lien entre l'humeur et la productivité&nbsp;: un aspect
                    que les dirigeants négligent.
                  </>,
                  <>
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      리더들이
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      간과하는
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      감정과
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      생산성의
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      연관성
                    </span>
                  </>,
                  selectedLanguage
                )}
              </h2>
              <p className={classes.pParagraph}>
                {translate(
                  <>
                    Most discussions about workplace music focus exclusively on
                    individual productivity metrics: focus, accuracy, and
                    processing speed. But music and employee morale share a
                    deeper relationship that extends to collective workplace
                    satisfaction, team dynamics, and organizational retention.
                  </>,
                  <>
                    La mayoría de los debates sobre la música en el lugar de
                    trabajo se centran exclusivamente en las métricas de
                    productividad individual: concentración, precisión y
                    velocidad de procesamiento. Sin embargo, la música y la
                    moral de los empleados comparten una relación más profunda
                    que se extiende a la satisfacción colectiva en el trabajo,
                    la dinámica de equipo y la retención de personal en la
                    organización.
                  </>,
                  <>
                    La plupart des discussions sur la musique au travail se
                    concentrent exclusivement sur les indicateurs de
                    productivité individuelle&nbsp;: concentration, précision et
                    vitesse de traitement. Or, la musique et le moral des
                    employés entretiennent une relation plus profonde qui influe
                    sur la satisfaction collective au travail, la dynamique
                    d'équipe et la fidélisation du personnel.
                  </>,
                  <>
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      직장
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      내
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      음악에
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      대한
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      대부분의
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      논의는
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      집중력,
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      정확성,
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      처리
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      속도와
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      같은
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      개인
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      생산성
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      지표에만
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      초점을
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      맞춥니다.
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      그러나
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      음악과
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      직원
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      사기는
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      더
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      깊은
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      관계를
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      맺고
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      있으며,
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      이는
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      직장
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      만족도,
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      팀
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      역학,
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      그리고
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      조직
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      유지율에까지
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      영향을
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      미칩니다.
                    </span>
                  </>,
                  selectedLanguage
                )}
              </p>
              <section id="employee-satisfaction-statistics">
                <h3 className={classes.subSection}>
                  {translate(
                    <>The Employee Satisfaction Statistics</>,
                    <>Estadísticas de satisfacción de los empleados</>,
                    <>Les statistiques de satisfaction des employés</>,
                    <>
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        직원
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        만족도
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        통계
                      </span>
                    </>,
                    selectedLanguage
                  )}
                </h3>
                <p className={classes.pParagraph}>
                  {translate(
                    <>
                      A comprehensive survey of 1,005 employees across multiple
                      industries revealed striking patterns about workplace
                      audio environments and job satisfaction:
                    </>,
                    <>
                      Una encuesta exhaustiva realizada a 1.005 empleados de
                      diversos sectores reveló patrones sorprendentes sobre los
                      entornos sonoros en el lugar de trabajo y la satisfacción
                      laboral:
                    </>,
                    <>
                      Une enquête approfondie menée auprès de 1&nbsp;005
                      employés de divers secteurs d'activité a révélé des
                      tendances frappantes concernant l'environnement sonore au
                      travail et la satisfaction professionnelle&nbsp;:
                    </>,
                    <>
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        다양한
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        산업
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        분야에
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        걸쳐
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        1,005명의
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        직원을
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        대상으로
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        실시한
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        종합
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        설문조사에서
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        직장
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        내
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        음향
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        환경과
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        직무
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        만족도에
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        대한
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        놀라운
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        패턴이
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        드러났습니다.
                      </span>
                    </>,
                    selectedLanguage
                  )}
                </p>
                <ol className={classes.standardText + ' ' + ' ol-list-white'}>
                  <li
                    className={classes.liOlPoint}
                    style={{
                      marginBottom: '15px',
                    }}
                  >
                    {translate(
                      <>
                        <span className={classes.spanBolder}>
                          80% believed music increased their workplace
                          productivity.
                        </span>{' '}
                        This isn’t just correlation&nbsp;— when four out of five
                        employees perceive a direct productivity benefit, the
                        psychological impact alone justifies implementation.
                      </>,
                      <>
                        <span className={classes.spanBolder}>
                          El 80% creía que la música aumentaba su productividad
                          en el trabajo.
                        </span>{' '}
                        Esto no es solo una correlación: cuando cuatro de cada
                        cinco empleados perciben un beneficio directo en la
                        productividad, el impacto psicológico por sí solo
                        justifica su implementación.
                      </>,
                      <>
                        <span className={classes.spanBolder}>
                          80&nbsp;% des personnes interrogées pensent que la
                          musique améliore leur productivité au travail.
                        </span>{' '}
                        Il ne s'agit pas d'une simple corrélation&nbsp;: lorsque
                        quatre employés sur cinq perçoivent un bénéfice direct
                        en termes de productivité, l'impact psychologique à lui
                        seul justifie la mise en place de cette pratique.
                      </>,
                      <>
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          응답자의
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          80%는
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          음악이
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          업무
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          생산성을
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          향상시킨다고
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          믿었습니다.
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          이는
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          단순한
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          상관관계가
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          아닙니다.
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          직원
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          5명
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          중
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          4명이
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          직접적인
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          생산성
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          향상
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          효과를
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          체감한다면,
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          심리적인
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          영향만으로도
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          음악
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          도입을
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          정당화할
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          수
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          있습니다.
                        </span>
                      </>,
                      selectedLanguage
                    )}
                  </li>
                  <li
                    className={classes.liOlPoint}
                    style={{
                      marginBottom: '15px',
                    }}
                  >
                    {translate(
                      <>
                        <span className={classes.spanBolder}>
                          56% used headphones regularly at work.
                        </span>{' '}
                        This suggests that even in offices without strategic
                        music programs, employees instinctively seek audio
                        optimization. The question becomes: will your
                        organization provide that optimization, or leave it to
                        individual solutions that might create communication
                        barriers?
                      </>,
                      <>
                        <span className={classes.spanBolder}>
                          El 56% utilizaba auriculares con regularidad en el
                          trabajo.
                        </span>{' '}
                        Esto sugiere que, incluso en oficinas sin programas de
                        música estratégicos, los empleados buscan
                        instintivamente optimizar su entorno sonoro. La pregunta
                        es: ¿su organización proporcionará esa optimización o la
                        dejará en manos de soluciones individuales que podrían
                        crear barreras de comunicación?
                      </>,
                      <>
                        <span className={classes.spanBolder}>
                          56&nbsp;% des employés utilisent régulièrement des
                          écouteurs au travail.
                        </span>{' '}
                        Cela suggère que même dans les bureaux dépourvus de
                        programmes musicaux stratégiques, les employés
                        recherchent instinctivement une optimisation sonore. La
                        question est donc la suivante&nbsp;: votre organisation
                        fournira-t-elle cette optimisation, ou la
                        laissera-t-elle à des solutions individuelles
                        susceptibles de créer des obstacles à la
                        communication&nbsp;?
                      </>,
                      <>
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          응답자의
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          56%가
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          직장에서
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          정기적으로
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          헤드폰을
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          사용한다고
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          답했습니다.
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          이는
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          전략적인
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          음악
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          프로그램이
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          없는
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          사무실에서도
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          직원들이
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          본능적으로
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          오디오
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          환경
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          최적화를
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          추구한다는
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          것을
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          시사합니다.
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          이제
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          문제는
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          다음과
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          같습니다.
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          귀사의
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          조직은
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          이러한
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          최적화를
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          제공할
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          것인가,
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          아니면
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          소통에
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          장애를
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          초래할
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          수
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          있는
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          개인적인
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          해결책에
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          맡길
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          것인가?
                        </span>
                      </>,
                      selectedLanguage
                    )}
                  </li>
                  <li
                    className={classes.liOlPoint}
                    style={{
                      marginBottom: '15px',
                    }}
                  >
                    {translate(
                      <>
                        <span className={classes.spanBolder}>
                          42% listened to some form of media the entire workday.
                        </span>{' '}
                        This demonstrates that sustained audio engagement
                        doesn’t lead to fatigue or diminished returns for nearly
                        half of workers.
                      </>,
                      <>
                        <span className={classes.spanBolder}>
                          El 42% escuchó algún tipo de contenido multimedia
                          durante toda la jornada laboral.
                        </span>{' '}
                        Esto demuestra que la exposición continua a contenido de
                        audio no provoca fatiga ni una disminución del
                        rendimiento en casi la mitad de los trabajadores.
                      </>,
                      <>
                        <span className={classes.spanBolder}>
                          42&nbsp;% des personnes interrogées ont écouté un
                          contenu multimédia pendant toute leur journée de
                          travail.
                        </span>{' '}
                        Cela démontre qu'une écoute prolongée ne provoque ni
                        fatigue ni baisse de productivité chez près de la moitié
                        des travailleurs.
                      </>,
                      <>
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          42%는
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          근무
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          시간
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          내내
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          어떤
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          형태로든
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          미디어를
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          시청하거나
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          청취했습니다.
                        </span>
                      </>,
                      selectedLanguage
                    )}
                  </li>
                  <li
                    className={classes.liOlPoint}
                    style={{
                      marginBottom: '15px',
                    }}
                  >
                    {translate(
                      <>
                        <span className={classes.spanBolder}>
                          46% wore headphones specifically to avoid
                          conversations.
                        </span>{' '}
                        This statistic reveals a potential challenge: personal
                        audio can create isolation and communication breakdowns.
                        Strategic office-wide music programs can provide focus
                        benefits while maintaining team connectivity.
                      </>,
                      <>
                        <span className={classes.spanBolder}>
                          El 46% usaba auriculares específicamente para evitar
                          conversaciones.
                        </span>{' '}
                        Esta estadística revela un posible problema: el audio
                        personal puede generar aislamiento y dificultades en la
                        comunicación. Los programas de música estratégicos para
                        toda la oficina pueden mejorar la concentración a la vez
                        que mantienen la conexión entre los miembros del equipo.
                      </>,
                      <>
                        <span className={classes.spanBolder}>
                          46&nbsp;% des personnes interrogées portaient des
                          écouteurs spécifiquement pour éviter les
                          conversations.
                        </span>{' '}
                        Cette statistique révèle un défi potentiel&nbsp;:
                        l'écoute audio personnelle peut engendrer l'isolement et
                        des problèmes de communication. Des programmes musicaux
                        stratégiques à l'échelle de l'entreprise peuvent
                        favoriser la concentration tout en maintenant la
                        cohésion d'équipe.
                      </>,
                      <>
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          46%가
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          대화를
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          피하기
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          위해
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          이어폰을
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          착용했습니다.
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          이
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          통계는
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          잠재적인
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          문제점을
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          보여줍니다.
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          개인용
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          오디오
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          기기는
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          고립감을
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          유발하고
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          소통
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          단절을
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          초래할
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          수
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          있습니다.
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          전략적인
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          사무실
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          전체
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          음악
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          프로그램은
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          팀원
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          간의
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          연결성을
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          유지하면서
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          집중력
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          향상이라는
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          이점을
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          제공할
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          수
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          있습니다.
                        </span>
                      </>,
                      selectedLanguage
                    )}
                  </li>
                </ol>
              </section>
            </section>
            <section id="different-industry-variations-on-music">
              <h2 className={classes.subSection}>
                {translate(
                  <>Industry Variations in Music Perception</>,
                  <>
                    Variaciones en la percepción musical según el sector
                    industrial
                  </>,
                  <>Variations sectorielles dans la perception de la musique</>,
                  <>
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      음악
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      인식에
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      있어서
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      산업별
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      차이점
                    </span>
                  </>,
                  selectedLanguage
                )}
              </h2>
              <p className={classes.pParagraph}>
                {translate(
                  <>
                    Not all industries experience music’s benefits equally. The
                    survey revealed fascinating patterns:
                  </>,
                  <>
                    No todas las industrias experimentan los beneficios de la
                    música por igual. La encuesta reveló patrones fascinantes:
                  </>,
                  <>
                    Toutes les industries ne bénéficient pas des avantages de la
                    musique de la même manière. L'enquête a révélé des tendances
                    fascinantes&nbsp;:
                  </>,
                  <>
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      모든
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      산업이
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      음악의
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      이점을
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      똑같이
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      누리는
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      것은
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      아닙니다.
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      설문조사
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      결과는
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      흥미로운
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      패턴을
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      보여주었습니다.
                    </span>
                  </>,
                  selectedLanguage
                )}
              </p>
              <p className={classes.pParagraph}>
                {translate(
                  <>Industries reporting highest productivity beliefs:</>,
                  <>
                    Sectores que reportan las mayores expectativas de
                    productividad:
                  </>,
                  <>
                    Secteurs d'activité affichant les plus fortes convictions en
                    matière de productivité&nbsp;:
                  </>,
                  <>
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      생산성
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      향상에
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      대한
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      기대치가
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      가장
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      높은
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      산업
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      분야는
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      다음과
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      같습니다.
                    </span>
                  </>,
                  selectedLanguage
                )}
              </p>
              <ul className={classes.standardText + ' ' + classes.ulArticles}>
                <li className={classes.liArticle}>
                  {translate(
                    <>Information services (software, tech, data)</>,
                    <>Servicios de información (software, tecnología, datos)</>,
                    <>
                      Services d'information (logiciels, technologies, données)
                    </>,
                    <>
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        정보
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        서비스(소프트웨어,
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        기술,
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        데이터)
                      </span>
                    </>,
                    selectedLanguage
                  )}
                </li>
                <li className={classes.liArticle}>
                  {translate(
                    <>Wholesale/retail operations</>,
                    <>Operaciones mayoristas/minoristas</>,
                    <>Opérations de vente en gros et au détail</>,
                    <>
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        도매/소매
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        운영
                      </span>
                    </>,
                    selectedLanguage
                  )}
                </li>
                <li className={classes.liArticle}>
                  {translate(
                    <>Healthcare (administrative, not clinical)</>,
                    <>Atención médica (administrativa, no clínica)</>,
                    <>Soins de santé (aspects administratifs, non cliniques)</>,
                    <>
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        의료
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        서비스
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        (임상
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        관련이
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        아닌
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        행정
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        분야)
                      </span>
                    </>,
                    selectedLanguage
                  )}
                </li>
              </ul>
              <p className={classes.subSection}>
                {translate(
                  <>Why these sectors respond positively:</>,
                  <>Por qué estos sectores responden positivamente:</>,
                  <>Pourquoi ces secteurs réagissent-ils positivement&nbsp;:</>,
                  <>
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      이러한
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      부문들이
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      긍정적으로
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      반응하는
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      이유는
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      다음과
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      같습니다.
                    </span>
                  </>,
                  selectedLanguage
                )}
              </p>
              <p className={classes.pParagraph}>
                {translate(
                  <>
                    These industries typically blend repetitive tasks with
                    customer interaction, requiring both sustained focus and
                    emotional regulation. Music provides cognitive support for
                    routine work while helping employees maintain positive
                    affect during customer-facing activities.
                  </>,
                  <>
                    Estas industrias suelen combinar tareas repetitivas con
                    interacción con el cliente, lo que requiere tanto
                    concentración sostenida como regulación emocional. La música
                    brinda apoyo cognitivo para el trabajo rutinario, a la vez
                    que ayuda a los empleados a mantener una actitud positiva
                    durante las actividades de atención al cliente.
                  </>,
                  <>
                    Ces secteurs d'activité combinent généralement des tâches
                    répétitives et des interactions avec la clientèle, ce qui
                    exige à la fois une concentration soutenue et une bonne
                    gestion des émotions. La musique apporte un soutien cognitif
                    pour les tâches routinières tout en aidant les employés à
                    conserver une attitude positive lors des interactions avec
                    les clients.
                  </>,
                  <>
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      이러한
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      산업
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      분야는
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      일반적으로
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      반복적인
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      작업과
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      고객
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      응대가
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      결합되어
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      있어
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      지속적인
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      집중력과
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      감정
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      조절
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      능력이
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      모두
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      필요합니다.
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      음악은
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      반복적인
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      업무에
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      대한
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      인지적
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      지원을
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      제공하는
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      동시에
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      직원들이
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      고객
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      응대
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      활동
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      중에
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      긍정적인
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      감정을
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      유지하도록
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      돕습니다.
                    </span>
                  </>,
                  selectedLanguage
                )}
              </p>
              <p className={classes.pParagraph}>
                {translate(
                  <>
                    Healthcare administrative staff, for example, must process
                    extensive documentation while maintaining compassion fatigue
                    resistance. Music supports both needs simultaneously.
                  </>,
                  <>
                    El personal administrativo del sector sanitario, por
                    ejemplo, debe procesar una gran cantidad de documentación a
                    la vez que mantiene la capacidad de resistir el agotamiento
                    emocional. La música contribuye a satisfacer ambas
                    necesidades simultáneamente.
                  </>,
                  <>
                    Le personnel administratif du secteur de la santé, par
                    exemple, doit traiter une documentation volumineuse tout en
                    résistant à l'épuisement émotionnel. La musique répond
                    simultanément à ces deux besoins.
                  </>,
                  <>
                    <span
                      aria-hidden={true}
                      className={classes.asianTextBlock}
                    ></span>
                  </>,
                  selectedLanguage
                )}
              </p>
              <section id="retention-wellbeing-roi">
                <h3 className={classes.subSection}>
                  {translate(
                    <>The Retention and Wellbeing ROI</>,
                    <>El retorno de la inversión en retención y bienestar</>,
                    <>
                      Le retour sur investissement en matière de fidélisation et
                      de bien-être
                    </>,
                    <>
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        예를
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        들어,
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        의료
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        행정
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        직원들은
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        방대한
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        양의
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        서류
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        작업을
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        처리하는
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        동시에
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        감정
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        소진에
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        대한
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        저항력을
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        유지해야
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        합니다.
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        음악은
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        이러한
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        두
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        가지
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        요구
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        사항을
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        동시에
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        충족시켜
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        줍니다.
                      </span>
                    </>,
                    selectedLanguage
                  )}
                </h3>
                <p className={classes.pParagraph}>
                  {translate(
                    <>
                      Forward-thinking companies recognize that strategic
                      workplace music programs correlate with measurable
                      business outcomes beyond immediate productivity:
                    </>,
                    <>
                      Las empresas con visión de futuro reconocen que los
                      programas estratégicos de música en el lugar de trabajo se
                      correlacionan con resultados empresariales medibles que
                      van más allá de la productividad inmediata:
                    </>,
                    <>
                      Les entreprises visionnaires reconnaissent que les
                      programmes musicaux stratégiques sur le lieu de travail
                      sont corrélés à des résultats commerciaux mesurables,
                      au-delà de la simple productivité immédiate&nbsp;:
                    </>,
                    <>
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        미래지향적인
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        기업들은
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        전략적인
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        직장
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        음악
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        프로그램이
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        즉각적인
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        생산성
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        향상을
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        넘어
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        측정
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        가능한
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        비즈니스
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        성과와
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        밀접한
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        관련이
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        있다는
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        것을
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        인식하고
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        있습니다.
                      </span>
                    </>,
                    selectedLanguage
                  )}
                </p>
                <p className={classes.pParagraph}>
                  {translate(
                    <>
                      ✅{' '}
                      <span className={classes.spanBolder}>
                        Reduced absenteeism rates:
                      </span>{' '}
                      Employees experiencing lower workplace stress take fewer
                      sick days. One study found that organizations with active
                      wellness programs (including environmental optimization
                      like music) saw 28% reduction in stress-related absences.
                    </>,
                    <>
                      ✅{' '}
                      <span className={classes.spanBolder}>
                        Reducción de las tasas de absentismo:
                      </span>{' '}
                      Los empleados que experimentan menos estrés en el trabajo
                      se ausentan menos por enfermedad. Un estudio reveló que
                      las organizaciones con programas de bienestar activos (que
                      incluyen la optimización del entorno, como la música)
                      experimentaron una reducción del 28% en las ausencias
                      relacionadas con el estrés.
                    </>,
                    <>
                      ✅{' '}
                      <span className={classes.spanBolder}>
                        Réduction des taux d'absentéisme&nbsp;:
                      </span>{' '}
                      Les employés subissant moins de stress au travail prennent
                      moins de jours de congé maladie. Une étude a révélé que
                      les organisations dotées de programmes de bien-être actifs
                      (incluant l'optimisation de l'environnement de travail,
                      par exemple avec de la musique) constataient une réduction
                      de 28&nbsp;% des absences liées au stress.
                    </>,
                    <>
                      ✅{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        결근율
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        감소:
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        직장
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        스트레스가
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        적은
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        직원들은
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        병가를
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        덜
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        사용합니다.
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        한
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        연구에
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        따르면
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        (음악
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        감상과
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        같은
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        환경
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        개선을
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        포함한)
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        적극적인
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        건강
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        증진
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        프로그램을
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        운영하는
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        조직에서는
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        스트레스
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        관련
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        결근이
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        28%
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        감소한
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        것으로
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        나타났습니다.
                      </span>
                    </>,
                    selectedLanguage
                  )}
                </p>
                <p className={classes.pParagraph}>
                  {translate(
                    <>
                      ✅{' '}
                      <span className={classes.spanBolder}>
                        Higher company loyalty metrics:
                      </span>{' '}
                      When organizations invest in employee experience through
                      environmental design, employees perceive greater
                      organizational support, directly correlating with
                      retention.
                    </>,
                    <>
                      ✅{' '}
                      <span className={classes.spanBolder}>
                        Mayores índices de lealtad a la empresa:
                      </span>{' '}
                      Cuando las organizaciones invierten en la experiencia del
                      empleado a través del diseño del entorno laboral, los
                      empleados perciben un mayor apoyo organizacional, lo que
                      se correlaciona directamente con la retención de talento.
                    </>,
                    <>
                      ✅{' '}
                      <span className={classes.spanBolder}>
                        Des indicateurs de fidélité à l'entreprise plus
                        élevés&nbsp;:
                      </span>{' '}
                      Lorsque les organisations investissent dans l'expérience
                      des employés par le biais de l'aménagement des espaces de
                      travail, les employés perçoivent un soutien
                      organisationnel accru, ce qui a un impact direct sur la
                      fidélisation.
                    </>,
                    <>
                      ✅{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        더
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        높은
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        직원
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        충성도
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        지표:
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        기업이
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        환경
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        디자인을
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        통해
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        직원
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        경험에
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        투자하면
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        직원들은
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        조직의
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        지원을
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        더
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        많이
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        받는다고
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        느끼게
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        되며,
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        이는
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        직원
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        유지율
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        증가와
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        직접적인
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        관련이
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        있습니다.
                      </span>
                    </>,
                    selectedLanguage
                  )}
                </p>
                <p className={classes.pParagraph}>
                  {translate(
                    <>
                      ✅{' '}
                      <span className={classes.spanBolder}>
                        Better job satisfaction ratings:
                      </span>{' '}
                      Music contributes to the overall workplace atmosphere.
                      Employees who enjoy their physical work environment report
                      higher job satisfaction, independent of compensation or
                      role satisfaction.
                    </>,
                    <>
                      ✅{' '}
                      <span className={classes.spanBolder}>
                        Mejores índices de satisfacción laboral:
                      </span>{' '}
                      La música contribuye al ambiente general del lugar de
                      trabajo. Los empleados que disfrutan de su entorno laboral
                      físico reportan una mayor satisfacción laboral,
                      independientemente de la remuneración o la satisfacción
                      con su puesto.
                    </>,
                    <>
                      ✅{' '}
                      <span className={classes.spanBolder}>
                        Meilleure satisfaction au travail&nbsp;:
                      </span>{' '}
                      La musique contribue à l'ambiance générale au travail. Les
                      employés qui apprécient leur environnement de travail
                      physique déclarent une plus grande satisfaction
                      professionnelle, indépendamment de la rémunération ou de
                      la satisfaction liée au poste.
                    </>,
                    <>
                      ✅{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        더
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        나은
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        직무
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        만족도:
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        음악은
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        전반적인
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        직장
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        분위기에
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        긍정적인
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        영향을
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        미칩니다.
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        물리적인
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        근무
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        환경에
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        만족하는
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        직원들은
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        급여나
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        직무
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        만족도와는
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        별개로
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        더
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        높은
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        직무
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        만족도를
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        보이는
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        것으로
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        나타났습니다.
                      </span>
                    </>,
                    selectedLanguage
                  )}
                </p>
                <p className={classes.pParagraph}>
                  {translate(
                    <>
                      ✅{' '}
                      <span className={classes.spanBolder}>
                        Lower stress-related health claims:
                      </span>{' '}
                      Mental wellness through sound isn’t an abstract theory.
                      Over 89% of participants in music therapy studies reported
                      significant stress reduction. When workplace stress costs
                      U.S. businesses over $300 billion annually, even modest
                      stress reduction delivers substantial ROI.
                    </>,
                    <>
                      ✅{' '}
                      <span className={classes.spanBolder}>
                        Menor número de bajas por problemas de salud
                        relacionados con el estrés:
                      </span>
                      El bienestar mental a través del sonido no es una teoría
                      abstracta. Más del 89% de los participantes en estudios de
                      musicoterapia informaron una reducción significativa del
                      estrés. Dado que el estrés laboral cuesta a las empresas
                      estadounidenses más de 300 mil millones de dólares al año,
                      incluso una reducción moderada del estrés genera un
                      retorno de la inversión considerable.
                    </>,
                    <>
                      ✅
                      <span className={classes.spanBolder}>
                        Réduction des problèmes de santé liés au stress&nbsp;:
                      </span>
                      Le bien-être mental par le son n'est pas une théorie
                      abstraite. Plus de 89&nbsp;% des participants à des études
                      de musicothérapie ont signalé une réduction significative
                      du stress. Sachant que le stress au travail coûte aux
                      entreprises américaines plus de 300 milliards de dollars
                      par an, même une réduction modeste du stress représente un
                      retour sur investissement considérable.
                    </>,
                    <>
                      ✅{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        스트레스
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        관련
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        건강
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        문제
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        발생률
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        감소:
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        소리를
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        통한
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        정신
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        건강
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        증진은
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        추상적인
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        이론이
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        아닙니다.
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        음악
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        치료
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        연구
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        참가자의
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        89%
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        이상이
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        상당한
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        스트레스
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        감소
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        효과를
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        경험했다고
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        보고했습니다.
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        직장
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        스트레스로
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        인해
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        미국
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        기업들이
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        매년
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        3천억
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        달러
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        이상의
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        손실을
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        입는다는
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        점을
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        고려할
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        때,
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        사소한
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        스트레스
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        감소조차도
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        상당한
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        투자
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        수익률(ROI)을
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        가져다줄
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        수
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        있습니다.
                      </span>
                    </>,
                    selectedLanguage
                  )}
                </p>
              </section>
            </section>
            <section id="psychological-relief-paradox">
              <h2 className={classes.subSection}>
                {translate(
                  <>The Psychological Relief Paradox</>,
                  <>La paradoja del alivio psicológico</>,
                  <>Le paradoxe du soulagement psychologique</>,
                  <>
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      심리적
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      안도감의
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      역설
                    </span>
                  </>,
                  selectedLanguage
                )}
              </h2>
              <p className={classes.pParagraph}>
                {translate(
                  <>
                    Research reveals a fascinating phenomenon: employees with
                    access to appropriate{' '}
                    <a
                      className={classes.aHref}
                      target="_blank"
                      href={PRACTICAL_STREAM_RESTAURANT_HERO_PAGE_URL}
                      title={
                        'Background Music that Improves Employee Productivity'
                      }
                      rel="noopener noreferrer"
                    >
                      background music report experiencing psychological{' '}
                      <span className={classes.spanPre}>
                        relief&nbsp;
                        <IconExternalLink />
                      </span>
                    </a>{' '}
                    and temporarily disconnecting from work-related
                    stress&nbsp;— even while actively working.
                  </>,
                  <>
                    Las investigaciones revelan un fenómeno fascinante: los
                    empleados que tienen acceso a música de fondo adecuada
                    afirman{' '}
                    <a
                      className={classes.aHref}
                      target="_blank"
                      href={PRACTICAL_STREAM_RESTAURANT_HERO_PAGE_URL_ES}
                      title={
                        'Música de fondo que mejora la productividad de los empleados'
                      }
                      rel="noopener noreferrer"
                    >
                      música de fondo adecuada afirman experimentar alivio{' '}
                      <span className={classes.spanPre}>
                        psicológico&nbsp;
                        <IconExternalLink />
                      </span>
                    </a>{' '}
                    y desconectarse temporalmente del estrés laboral, incluso
                    mientras están trabajando activamente.
                  </>,
                  <>
                    Des recherches révèlent un phénomène fascinant&nbsp;: les
                    employés ayant accès à une{' '}
                    <a
                      className={classes.aHref}
                      target="_blank"
                      href={PRACTICAL_STREAM_RESTAURANT_HERO_PAGE_URL_FR}
                      title={
                        "Musique d'ambiance qui améliore la productivité des employés"
                      }
                      rel="noopener noreferrer"
                    >
                      musique d'ambiance appropriée déclarent ressentir un
                      soulagement{' '}
                      <span className={classes.spanPre}>
                        psychologique&nbsp;
                        <IconExternalLink />
                      </span>
                    </a>{' '}
                    et une déconnexion temporaire du stress lié au travail, même
                    lorsqu'ils sont en pleine activité professionnelle.
                  </>,
                  <>
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      연구
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      결과에
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      따르면
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      흥미로운
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      현상이
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      발견되었습니다.
                    </span>{' '}
                    <a
                      className={classes.aHref}
                      target="_blank"
                      href={PRACTICAL_STREAM_RESTAURANT_HERO_PAGE_URL_KO}
                      title={'직원 생산성 향상에 도움이 되는 배경 음악'}
                      rel="noopener noreferrer"
                    >
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        적절한
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        배경
                      </span>{' '}
                      <span className={classes.spanPre}>
                        음악을&nbsp;
                        <IconExternalLink />
                      </span>
                    </a>{' '}
                    <span
                      aria-hidden={true}
                      className={classes.asianTextBlock}
                    ></span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      들을
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      수
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      있는
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      직원들은
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      업무
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      중에도
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      심리적으로
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      안정감을
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      느끼고
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      업무
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      관련
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      스트레스에서
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      일시적으로
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      벗어나는
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      경험을
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      한다고
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      보고했습니다.
                    </span>
                  </>,
                  selectedLanguage
                )}
              </p>
              <p className={classes.pParagraph}>
                {translate(
                  <>
                    This seems paradoxical. How can you feel less stressed about
                    work while doing work?
                  </>,
                  <>
                    Esto parece paradójico. ¿Cómo es posible sentirse menos
                    estresado por el trabajo mientras se está trabajando?
                  </>,
                  <>
                    Cela semble paradoxal. Comment peut-on se sentir moins
                    stressé par le travail tout en étant en train de
                    travailler&nbsp;?
                  </>,
                  <>
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      이것은
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      역설적으로
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      들립니다.
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      어떻게
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      일을
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      하면서
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      스트레스를
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      덜
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      받을
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      수
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      있을까요?
                    </span>
                  </>,
                  selectedLanguage
                )}
              </p>
              <p className={classes.pParagraph}>
                {translate(
                  <>
                    The answer lies in music’s ability to create psychological
                    distance from stressors without reducing engagement. Music
                    provides a “third presence” in the workplace&nbsp;— it’s not
                    the demanding task, and it’s not the employee struggling
                    with the task. It’s a supportive environmental factor that
                    makes the struggle feel more bearable.
                  </>,
                  <>
                    La respuesta reside en la capacidad de la música para crear
                    una distancia psicológica respecto a los factores
                    estresantes sin disminuir la concentración. La música
                    proporciona una "tercera presencia" en el lugar de trabajo:
                    no es la tarea exigente, ni tampoco el empleado que lucha
                    con ella. Es un factor ambiental de apoyo que hace que la
                    dificultad resulte más llevadera.
                  </>,
                  <>
                    La réponse réside dans la capacité de la musique à créer une
                    distance psychologique par rapport aux facteurs de stress
                    sans pour autant diminuer l'implication. La musique
                    constitue une «&nbsp;troisième présence&nbsp;» sur le lieu
                    de travail&nbsp;: elle n'est ni la tâche exigeante, ni
                    l'employé aux prises avec cette tâche. C'est un élément
                    environnemental favorable qui rend la difficulté plus
                    supportable.
                  </>,
                  <>
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      그
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      해답은
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      음악이
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      스트레스
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      요인으로부터
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      심리적
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      거리를
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      두면서도
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      몰입도를
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      떨어뜨리지
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      않는
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      능력에
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      있습니다.
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      음악은
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      직장에서
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      "제3의
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      존재"
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      역할을
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      합니다.
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      그것은
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      부담스러운
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      업무도
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      아니고,
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      업무에
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      어려움을
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      겪는
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      직원도
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      아닙니다.
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      음악은
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      힘든
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      상황을
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      좀
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      더
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      견딜
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      만하게
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      만들어주는
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      지원적인
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      환경적
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      요소입니다.
                    </span>
                  </>,
                  selectedLanguage
                )}
              </p>
              <p className={classes.pParagraph}>
                {translate(
                  <>
                    Think of music as creating a buffer zone between the
                    employee and workplace stressors, similar to how a good
                    lighting design reduces eye strain without workers
                    consciously noticing.
                  </>,
                  <>
                    Conciba la música como una zona de amortiguación entre el
                    empleado y los factores estresantes del lugar de trabajo, de
                    forma similar a cómo un buen diseño de iluminación reduce la
                    fatiga visual sin que los trabajadores lo noten
                    conscientemente.
                  </>,
                  <>
                    Considérez la musique comme un moyen de créer une zone
                    tampon entre l'employé et les facteurs de stress liés au
                    travail, de la même manière qu'un bon éclairage réduit la
                    fatigue oculaire sans que les travailleurs ne s'en rendent
                    compte consciemment.
                  </>,
                  <>
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      음악은
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      직원과
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      직장
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      스트레스
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      요인
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      사이에
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      완충
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      지대를
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      만들어주는
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      역할을
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      한다고
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      생각해보세요.
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      마치
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      좋은
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      조명
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      디자인이
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      직원들이
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      의식하지
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      못하는
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      사이에
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      눈의
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      피로를
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      줄여주는
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      것과
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      같습니다.
                    </span>
                  </>,
                  selectedLanguage
                )}
              </p>
            </section>
            <section id="the-evidence-is-overwhelming">
              <h2 className={classes.subSection}>
                {translate(
                  <>The Evidence is Overwhelming: Music&nbsp;Works</>,
                  <>La evidencia es abrumadora: la&nbsp;música funciona</>,
                  <>
                    Les preuves sont accablantes&nbsp;: la musique est efficace
                  </>,
                  <>
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      증거는
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      압도적입니다:
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      음악은
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      효과가
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      있습니다.
                    </span>
                  </>,
                  selectedLanguage
                )}
              </h2>
              <p className={classes.pParagraph}>
                {translate(
                  <>
                    The neuroscience is unambiguous. Music triggers dopamine
                    release that enhances motivation and focus. It reduces
                    cortisol by up to 61%, fundamentally shifting your
                    physiological stress response.
                  </>,
                  <>
                    La neurociencia es concluyente. La música desencadena la
                    liberación de dopamina, lo que mejora la motivación y la
                    concentración. Reduce el cortisol hasta en un 61%,
                    modificando fundamentalmente la respuesta fisiológica al
                    estrés.
                  </>,
                  <>
                    Les neurosciences sont formelles&nbsp;: la musique déclenche
                    la libération de dopamine, ce qui améliore la motivation et
                    la concentration. Elle réduit le taux de cortisol jusqu'à
                    61&nbsp;%, modifiant ainsi profondément votre réponse
                    physiologique au stress.
                  </>,
                  <>
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      신경과학
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      연구
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      결과는
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      명확합니다.
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      음악은
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      도파민
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      분비를
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      촉진하여
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      동기
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      부여와
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      집중력을
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      향상시킵니다.
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      또한
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      코르티솔
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      수치를
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      최대
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      61%까지
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      감소시켜
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      생리적인
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      스트레스
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      반응을
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      근본적으로
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      변화시킵니다.
                    </span>
                  </>,
                  selectedLanguage
                )}
              </p>
              <p className={classes.pParagraph}>
                {translate(
                  <>
                    It activates ideal brain wave patterns for sustained
                    cognitive work. And 90% of workers demonstrate measurably
                    better performance with strategic music implementation.
                  </>,
                  <>
                    Activa patrones de ondas cerebrales ideales para un trabajo
                    cognitivo sostenido. Además, el 90% de los trabajadores
                    demuestran un rendimiento notablemente mejor con la
                    implementación estratégica de música.
                  </>,
                  <>
                    Elle active des schémas d'ondes cérébrales idéaux pour un
                    travail cognitif soutenu. Et 90&nbsp;% des travailleurs
                    obtiennent des performances nettement améliorées grâce à
                    l'intégration stratégique de la musique.
                  </>,
                  <>
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      이는
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      지속적인
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      인지
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      작업을
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      위한
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      이상적인
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      뇌파
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      패턴을
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      활성화합니다.
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      그리고
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      전략적인
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      음악
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      활용을
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      통해
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      근로자의
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      90%가
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      눈에
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      띄게
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      향상된
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      업무
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      성과를
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      보여줍니다.
                    </span>
                  </>,
                  selectedLanguage
                )}
              </p>
              <p className={classes.pParagraph}>
                {translate(
                  <>
                    The psychology is equally clear. Music impacts employee
                    morale, job satisfaction, retention, and overall well-being
                    in ways that extend far beyond individual productivity
                    metrics.
                  </>,
                  <>
                    Desde el punto de vista psicológico, la situación es
                    igualmente clara. La música influye en la moral de los
                    empleados, la satisfacción laboral, la retención del
                    personal y el bienestar general de maneras que van mucho más
                    allá de las métricas de productividad individual.
                  </>,
                  <>
                    Sur le plan psychologique, la situation est tout aussi
                    claire. La musique a un impact sur le moral des employés, la
                    satisfaction au travail, la fidélisation et le bien-être
                    général, d'une manière qui va bien au-delà des simples
                    indicateurs de productivité individuelle.
                  </>,
                  <>
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      심리학적인
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      측면에서도
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      그
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      효과는
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      명확합니다.
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      음악은
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      직원들의
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      사기,
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      직무
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      만족도,
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      근속률,
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      그리고
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      전반적인
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      복지에
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      영향을
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      미치며,
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      이는
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      개별
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      생산성
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      지표를
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      훨씬
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      뛰어넘는
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      범위에까지
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      영향을
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      줍니다.
                    </span>
                  </>,
                  selectedLanguage
                )}
              </p>
              <p className={classes.pParagraph}>
                {translate(
                  <>
                    But understanding the science is only half the equation. The
                    real challenge&nbsp;— and opportunity&nbsp;— lies in
                    implementation.
                  </>,
                  <>
                    Pero comprender la ciencia es solo la mitad de la ecuación.
                    El verdadero desafío, y la verdadera oportunidad, reside en
                    la implementación.
                  </>,
                  <>
                    Mais comprendre la science ne représente que la moitié du
                    problème. Le véritable défi&nbsp;– et la véritable
                    opportunité&nbsp;– réside dans la mise en œuvre.
                  </>,
                  <>
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      하지만
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      과학적
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      원리를
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      이해하는
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      것은
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      문제
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      해결의
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      절반에
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      불과합니다.
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      진정한
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      도전
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      과제이자
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      기회는
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      바로
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      실행에
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      있습니다.
                    </span>
                  </>,
                  selectedLanguage
                )}
              </p>
            </section>
            <section id="music-neuroscience-in-practical-application">
              <h2 className={classes.subSection}>
                {translate(
                  <>
                    How do you translate this neuroscience into a practical
                    workplace strategy?
                  </>,
                  <>
                    ¿Cómo se traduce esta información neurocientífica en una
                    estrategia práctica para el entorno laboral?
                  </>,
                  <>
                    Comment traduire ces découvertes en neurosciences en une
                    stratégie pratique pour le milieu de travail&nbsp;?
                  </>,
                  <>
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      이러한
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      신경과학적
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      지식을
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      어떻게
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      실용적인
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      직장
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      전략으로
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      전환할
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      수
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      있을까요?
                    </span>
                  </>,
                  selectedLanguage
                )}
              </h2>
              <p className={classes.pParagraph}>
                {translate(
                  <>
                    Which genres work best for your specific office environment?
                    How do you balance individual preferences with collective
                    benefit? What about remote teams, coworking spaces, and
                    hybrid work arrangements? How do you measure success and
                    iterate based on results?
                  </>,
                  <>
                    ¿Qué géneros musicales funcionan mejor en el entorno de su
                    oficina? ¿Cómo se concilian las preferencias individuales
                    con el beneficio colectivo? ¿Qué ocurre con los equipos
                    remotos, los espacios de coworking y los modelos de trabajo
                    híbridos? ¿Cómo se mide el éxito y se realizan ajustes en
                    función de los resultados?
                  </>,
                  <>
                    Quels genres musicaux conviennent le mieux à votre
                    environnement de travail&nbsp;? Comment concilier les
                    préférences individuelles et le bien-être collectif&nbsp;?
                    Qu'en est-il des équipes à distance, des espaces de
                    coworking et des modes de travail hybrides&nbsp;? Comment
                    mesurer le succès et ajuster la stratégie en fonction des
                    résultats&nbsp;?
                  </>,
                  <>
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      어떤
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      장르의
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      음악이
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      특정
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      사무실
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      환경에
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      가장
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      적합할까요? 개인의
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      선호도와
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      전체
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      구성원의
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      이익
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      사이에서
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      어떻게
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      균형을
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      맞춰야
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      할까요? 원격
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      근무
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      팀,
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      코워킹
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      스페이스,
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      하이브리드
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      근무
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      환경에서는
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      어떻게
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      해야
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      할까요? 성공
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      여부를
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      어떻게
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      측정하고
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      결과를
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      바탕으로
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      개선해
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      나갈
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      수
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      있을까요?
                    </span>
                  </>,
                  selectedLanguage
                )}
              </p>
            </section>
            <section id="teaser-on-next-article-in-series">
              <h2 className={classes.subSection}>
                {translate(
                  <>Ready for Part 2: Implementation Strategies</>,
                  <>Listos para la Parte 2: Estrategias de implementación</>,
                  <>
                    Prêts pour la deuxième partie&nbsp;: Stratégies de mise en
                    œuvre
                  </>,
                  <>
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      파트
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      2:
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      구현
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      전략을
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      위한
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      준비가
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      되셨나요?
                    </span>
                  </>,
                  selectedLanguage
                )}
              </h2>
              <p className={classes.pParagraph}>
                {translate(
                  <>
                    You now understand the remarkable science behind workplace
                    music&nbsp;— how it rewires neural pathways, regulates
                    stress hormones, and transforms both individual productivity
                    and collective morale.
                  </>,
                  <>
                    Ahora comprendes la extraordinaria ciencia que hay detrás de
                    la música en el lugar de trabajo: cómo reconfigura las vías
                    neuronales, regula las hormonas del estrés y transforma
                    tanto la productividad individual como la moral colectiva.
                  </>,
                  <>
                    Vous comprenez maintenant la science remarquable qui se
                    cache derrière la musique au travail&nbsp;: comment elle
                    modifie les connexions neuronales, régule les hormones du
                    stress et transforme à la fois la productivité individuelle
                    et le moral collectif.
                  </>,
                  <>
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      이제
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      여러분은
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      직장
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      음악
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      뒤에
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      숨겨진
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      놀라운
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      과학적
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      원리를
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      이해하셨을
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      것입니다.
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      음악이
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      신경
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      경로를
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      재구성하고,
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      스트레스
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      호르몬을
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      조절하며,
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      개인의
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      생산성과
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      조직
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      전체의
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      사기를
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      어떻게
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      변화시키는지
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      말입니다.
                    </span>
                  </>,
                  selectedLanguage
                )}
              </p>
              <p className={classes.pParagraph}>
                {translate(
                  <>
                    In Part 2, we’ll transform this science into action with:
                  </>,
                  <>
                    En la Parte 2, transformaremos esta teoría científica en
                    acción con:
                  </>,
                  <>
                    Dans la deuxième partie, nous allons transformer cette
                    théorie scientifique en actions concrètes grâce à&nbsp;:
                  </>,
                  <>
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      2부에서는
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      이러한
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      과학적
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      지식을
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      바탕으로
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      다음과
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      같은
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      실질적인
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      조치를
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      취할
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      것입니다.
                    </span>
                  </>,
                  selectedLanguage
                )}
              </p>
              <ul className={classes.standardText + ' ' + classes.ulArticles}>
                <li className={classes.liArticle}>
                  {translate(
                    <>
                      Zone-based audio design systems for traditional offices
                      that respect different work modes and personal preferences
                    </>,
                    <>
                      Sistemas de diseño de audio por zonas para oficinas
                      tradicionales que respetan los diferentes modos de trabajo
                      y las preferencias personales
                    </>,
                    <>
                      Systèmes de conception audio par zones pour les bureaux
                      traditionnels, respectant les différents modes de travail
                      et les préférences personnelles
                    </>,
                    <>
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        다양한
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        업무
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        방식과
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        개인적
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        선호를
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        고려하여
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        전통적인
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        사무실
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        환경에
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        적합한
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        구역
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        기반
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        오디오
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        디자인
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        시스템
                      </span>
                    </>,
                    selectedLanguage
                  )}
                </li>
                <li className={classes.liArticle}>
                  {translate(
                    <>
                      Strategic music approaches for coworking spaces that
                      balance diverse needs and prevent distraction
                    </>,
                    <>
                      Enfoques musicales estratégicos para espacios de coworking
                      que equilibren las diversas necesidades y eviten las
                      distracciones
                    </>,
                    <>
                      Approches musicales stratégiques pour les espaces de
                      coworking, permettant de concilier les besoins divers et
                      de prévenir les distractions
                    </>,
                    <>
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        다양한
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        요구
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        사항의
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        균형을
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        맞추고
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        방해
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        요소를
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        최소화하는
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        코워킹
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        스페이스를
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        위한
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        전략적인
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        음악
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        활용
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        방안
                      </span>
                    </>,
                    selectedLanguage
                  )}
                </li>
                <li className={classes.liArticle}>
                  {translate(
                    <>
                      Remote work soundtrack strategies that help distributed
                      teams maintain focus and connection
                    </>,
                    <>
                      Estrategias de ambientación musical para el trabajo remoto
                      que ayudan a los equipos distribuidos a mantener la
                      concentración y la conexión
                    </>,
                    <>
                      Stratégies musicales pour le télétravail&nbsp;: comment
                      aider les équipes distantes à maintenir leur concentration
                      et leur connexion
                    </>,
                    <>
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        원격
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        근무
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        팀이
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        집중력과
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        연결성을
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        유지하는
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        데
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        도움이
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        되는
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        원격
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        근무
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        환경을
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        위한
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        음악
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        활용
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        전략
                      </span>
                    </>,
                    selectedLanguage
                  )}
                </li>
                <li className={classes.liArticle}>
                  {translate(
                    <>
                      Your 4-step implementation plan with specific actions for
                      assessment, setup, launch, and optimization
                    </>,
                    <>
                      Su plan de implementación de 4 pasos con acciones
                      específicas para la evaluación, la configuración, el
                      lanzamiento y la optimización
                    </>,
                    <>
                      Votre plan de mise en œuvre en 4 étapes, avec des actions
                      spécifiques pour l'évaluation, la configuration, le
                      lancement et l'optimisation
                    </>,
                    <>
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        평가,
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        설정,
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        출시
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        및
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        최적화를
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        위한
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        구체적인
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        실행
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        계획을
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        포함한
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        4단계
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        구현
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        계획
                      </span>
                    </>,
                    selectedLanguage
                  )}
                </li>
                <li className={classes.liArticle}>
                  {translate(
                    <>
                      The 7 common mistakes that sabotage music programs (and
                      how to avoid them)
                    </>,
                    <>
                      Los 7 errores comunes que sabotean los programas de música
                      (y cómo evitarlos)
                    </>,
                    <>
                      Les 7 erreurs courantes qui sabotent les programmes
                      musicaux (et comment les éviter)
                    </>,
                    <>
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        음악
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        프로그램을
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        망치는
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        7가지
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        흔한
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        실수
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        (그리고
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        이를
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        피하는
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        방법)
                      </span>
                    </>,
                    selectedLanguage
                  )}
                </li>
                <li className={classes.liArticle}>
                  {translate(
                    <>
                      Week-by-week implementation checklist to get started
                      immediately
                    </>,
                    <>
                      Lista de verificación de implementación semana a semana
                      para comenzar de inmediato
                    </>,
                    <>
                      Liste de contrôle hebdomadaire pour une mise en œuvre
                      immédiate
                    </>,
                    <>
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        즉시
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        시작할
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        수
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        있도록
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        주별
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        실행
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        체크리스트를
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        제공합니다.
                      </span>
                    </>,
                    selectedLanguage
                  )}
                </li>
                <li className={classes.liArticle}>
                  {translate(
                    <>
                      Metrics and measurement frameworks to prove ROI and
                      iterate based on data
                    </>,
                    <>
                      Métricas y marcos de medición para demostrar el retorno de
                      la inversión y realizar mejoras basadas en datos.
                    </>,
                    <>
                      Des indicateurs et des cadres de mesure pour prouver le
                      retour sur investissement et itérer en se basant sur les
                      données
                    </>,
                    <>
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        투자
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        수익률(ROI)을
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        입증하고
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        데이터를
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        기반으로
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        개선하기
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        위한
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        지표
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        및
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        측정
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        프레임워크
                      </span>
                    </>,
                    selectedLanguage
                  )}
                </li>
              </ul>
              <p className={classes.pParagraph}>
                {translate(
                  <>
                    The science proves music works. Part 2 shows you exactly how
                    to make it work for your organization.
                  </>,
                  <>
                    La ciencia demuestra que la música funciona. La segunda
                    parte le mostrará exactamente cómo hacer que funcione para
                    su organización.
                  </>,
                  <>
                    La science prouve que la musique est efficace. La deuxième
                    partie vous explique précisément comment l'utiliser au
                    profit de votre organisation.
                  </>,
                  <>
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      과학적으로
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      음악의
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      효과가
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      입증되었습니다.
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      2부에서는
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      음악을
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      활용하여
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      조직에
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      효과를
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      극대화하는
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      방법을
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      정확하게
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      보여드립니다.
                    </span>
                  </>,
                  selectedLanguage
                )}
              </p>
            </section>
            <hr className={classes.threeDotDivisorBottomOfArticle} />
          </Grid.Col>
          <Grid.Col md={24} pl={20} pr={20}>
            <BackToArticle
              selectedLanguage={selectedLanguage}
              navigateRoute={props?.navigateRoute}
            />
            <br />
          </Grid.Col>
          <Grid.Col md={24} pl={20} pr={20}>
            <ReadyToTransform
              selectedLanguage={selectedLanguage}
              linkVariant={false}
            />
          </Grid.Col>
          <Grid.Col md={24}>
            <div>
              <br />
              <br />
              <br />
            </div>
          </Grid.Col>
        </Grid>
      </div>
    </div>
  )
}
